import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { ClosedPeriodNotice, useClosedPeriodInfo } from '../components/period/ClosedPeriodNotice';
import { useStepUp } from '../components/auth/StepUpProvider';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import apiClient from '../utils/apiClient';
import { usePlaidLink } from 'react-plaid-link';
import OpenBankingConsentDialog from '../components/OpenBankingConsentDialog';
import { useMonoConnect } from '../hooks/useMonoConnect';
import { MonoSyncProgress } from '../components/shared/MonoSyncProgress';
import { BankFeedSyncBanner } from '../components/shared/BankFeedSyncBanner';
// iter959 post-signoff - NigeriaBankTile no longer imported here; the per-account
// "Reconnect" panel it rendered was removed. The unified "Update bank
// transactions" banner + the 3-dot menu now cover its role.
import MonoBanksRefreshSection from '../components/banking/MonoBanksRefreshSection';
import RetryBacklogButton from '../components/banking/RetryBacklogButton';
import RetryBacklogConfirmButton from '../components/banking/RetryBacklogConfirmButton';
import BankGapBanner from '../components/banking/BankGapBanner';
import HistoryGapBanner from '../components/banking/HistoryGapBanner';
import PlaidAccountTile from '../components/banking/PlaidAccountTile';
import PlaidReauthBanner from '../components/banking/PlaidReauthBanner';
import RestoredReconnectBanner from '../components/banking/RestoredReconnectBanner';
import PlaidBackfillProgress from '../components/banking/PlaidBackfillProgress';
import StatementUploadDialog from '../components/banking/StatementUploadDialog';
import { StatementUploadDialog as UploadStarterDialog } from '../components/bankfeed/StatementUploadDialog';
import { PostDeleteGapFillModal } from '../components/bankfeed/PostDeleteGapFillModal';
import StatementVaultDrawer from '../components/banking/StatementVaultDrawer';
import { CONNECT_FALLBACK_KEY, recordConnectAbandoned } from '../hooks/useMonoConnect';
import { useConnectUnconfirmed, recordConnectUnconfirmed, clearConnectUnconfirmed, markMonoRedirect, settleAbandonedMonoReturn } from '../utils/connectUnconfirmed';
import { ConnectUnconfirmedNote } from '../components/banking/ConnectUnconfirmedNote';
import { SetOpeningBalanceDialog } from '../components/banking/SetOpeningBalanceDialog';
import AddBankAccountModal from '../components/AddBankAccountModal';
// iter1213 - per-account pill that opens the mandatory OB confirmation
// dialog when the completeness gates have deferred derivation.
import { PendingOBAccountPill } from '../components/banking/PendingOpeningBalanceTaskBar';
import ConfirmDestructive from '../components/common/ConfirmDestructive';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Building2, Plus, Unlink, RefreshCw, Upload, FileText, Wallet, AlertCircle,
  CheckCircle2, Loader2, Globe, ExternalLink, MoreVertical, Receipt, 
  CreditCard, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownLeft,
  Eye, Edit, Trash2, PlusCircle, Sparkles, Brain, Zap, ChevronRight, 
  Activity, Clock, CheckCircle, Link2, ArrowLeftRight, XCircle,
  CornerDownRight,
} from 'lucide-react';
import { BankIntegrationTooltip, SUPPORTED_BANKS_BY_COUNTRY, getCountryDisplayName } from '../components/BankIntegrationTooltip';
import { InterBankTransferModal } from '../components/bankfeed/InterBankTransferModal';
import { ClassificationProgressTracker } from '../components/ClassificationProgressTracker';
import MonoBackfillProgress from '../components/banking/MonoBackfillProgress';
import { BankFeedProvider, useBankFeedData } from '../contexts/BankFeedContext';
import { useBackgroundJobs } from '../contexts/BackgroundJobsContext';
import { useStatementUploader } from '../hooks/useStatementUploader';
import { triggerBacklogRefresh } from '../components/banking/GlobalBacklogModalProvider';
// iter1207 - BacklogConfirmDialog is now mounted globally via
// GlobalBacklogModalProvider (see components/layout/DashboardLayout.jsx).
// eslint-disable-next-line no-unused-vars
import { BacklogConfirmDialog } from '../components/shared/BacklogConfirmDialog';
import { BankConnectProgress } from '../components/shared/BankConnectProgress';
import PendingPlaidResumeBanner from '../components/banking/PendingPlaidResumeBanner';
import { isIOSNative } from '../utils/nativeBridge';
import {
  savePlaidLinkToken,
  clearPlaidLinkToken,
} from '../lib/plaidLinkStorage';

// Plaid Link Button Component
const PlaidLinkButton = ({ onSuccess, disabled, children, className }) => {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchLinkToken = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/plaid/create-link-token`);
      setLinkToken(response.data.link_token);
      // iter 908 - Persist the link_token so the /oauth/plaid resume route can
      // recover it after the bank's OAuth round-trip. Cleared on success/exit.
      // iter1115 - moved from sessionStorage to localStorage (30-min TTL)
      // because iOS PWAs drop sessionStorage on cross-origin OAuth redirects.
      savePlaidLinkToken(response.data.link_token, window.location.pathname);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize bank connection');
    } finally {
      setLoading(false);
    }
  };

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: (public_token, metadata) => {
      clearPlaidLinkToken();
      onSuccess(public_token, metadata);
    },
    onExit: (err) => {
      if (err) toast.error('Bank connection was cancelled');
      clearPlaidLinkToken();
      setLinkToken(null);
    },
  });

  useEffect(() => {
    if (linkToken && ready) open();
  }, [linkToken, ready, open]);

  return (
    <Button 
      className={className || "gap-2 bg-blue-600 hover:bg-blue-700"}
      onClick={fetchLinkToken}
      disabled={disabled || loading}
      data-testid="plaid-connect-btn"
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
      {children || 'Connect Bank'}
    </Button>
  );
};

// Currency formatter
const formatCurrency = (amount, currency = 'NGN') => {
  const symbols = { NGN: '₦', GBP: '£', USD: '$', EUR: '€' };
  const symbol = symbols[currency] || currency + ' ';
  return `${symbol}${(amount || 0).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// iter1108 - Small helper that opens Plaid Link in update-mode with
// Account Select enabled. Mounted only when a link_token is present, so
// the hook has a valid token before it initialises. On success, delegates
// to the parent's `onSyncAccounts` (which calls /sync-accounts and
// refetches the bank list).
//
// iter1115 - Forward `public_token` + `metadata` on onSuccess. When this
// launcher is reused for the row-level "Connect Bank" flow
// (`startPlaidRowConnect`), the parent callback exchanges the public
// token. Dropping the args here silently broke every Plaid reconnect
// launched from a bank row's 3-dots menu.
const PlaidManageAccountsLauncher = ({ linkToken, onSuccess, onExit }) => {
  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: (public_token, metadata) => {
      clearPlaidLinkToken();
      onSuccess(public_token, metadata);
    },
    onExit: (err) => {
      clearPlaidLinkToken();
      onExit(err);
    },
  });
  React.useEffect(() => {
    if (linkToken && ready) open();
  }, [linkToken, ready, open]);
  return null;
};

// Compact currency formatter for KPI cards - prevents very large balances
// (millions / billions / trillions) from overflowing fixed-width tiles.
// Always pair with a `title=` tooltip rendering the exact value so the user
// can hover to see the precise figure.
const formatCompactCurrency = (amount, currency = 'NGN') => {
  const symbols = { NGN: '₦', GBP: '£', USD: '$', EUR: '€' };
  const symbol = symbols[currency] || currency + ' ';
  const num = parseFloat(amount) || 0;
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  // Below 1M → full precision still fits comfortably at text-3xl.
  if (abs < 1e6) {
    return `${symbol}${num.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  let value;
  let suffix;
  if (abs >= 1e15) { value = abs / 1e15; suffix = 'Q'; }
  else if (abs >= 1e12) { value = abs / 1e12; suffix = 'T'; }
  else if (abs >= 1e9)  { value = abs / 1e9;  suffix = 'B'; }
  else                   { value = abs / 1e6;  suffix = 'M'; }

  // Strip trailing zeros: 411.30M → 411.3M, 1.00B → 1B.
  const trimmed = parseFloat(value.toFixed(2)).toString();
  return `${sign}${symbol}${trimmed}${suffix}`;
};

// Country flag helper
const getCountryFlag = (country) => {
  const flags = {
    'NG': '🇳🇬', 'GB': '🇬🇧', 'US': '🇺🇸', 'DE': '🇩🇪', 'FR': '🇫🇷', 'NL': '🇳🇱',
    'IE': '🇮🇪', 'IT': '🇮🇹', 'ES': '🇪🇸', 'BE': '🇧🇪', 'AT': '🇦🇹', 'DK': '🇩🇰',
    'FI': '🇫🇮', 'EE': '🇪🇪', 'LV': '🇱🇻', 'LT': '🇱🇹', 'GH': '🇬🇭', 'KE': '🇰🇪',
    'TZ': '🇹🇿', 'ZA': '🇿🇦'
  };
  return flags[country] || '🏦';
};

// Get country name for display
const getCountryName = (code) => {
  const names = {
    'NG': 'Nigerian', 'GB': 'UK', 'US': 'US', 'GH': 'Ghanaian', 'KE': 'Kenyan',
    'TZ': 'Tanzanian', 'ZA': 'South African'
  };
  return names[code] || '';
};

// Use shared supported banks from BankIntegrationTooltip component
const SUPPORTED_BANKS = Object.fromEntries(
  Object.entries(SUPPORTED_BANKS_BY_COUNTRY).map(([code, data]) => [code, data.banks || []])
);

const MyBanksPage = () => {
  const { user, business } = useAuth();
  const { requireStepUp } = useStepUp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const closedPeriodInfo = useClosedPeriodInfo('banks');
  // iter1230 - global background-job tracker so statement uploads triggered
  // from the Banks page get a progress card + completion toast on ANY page
  // (previously the job_id was dropped on the floor and the user saw no
  // progress after navigating to /bank-feeds).
  const backgroundJobsCtx = useBackgroundJobs();
  const { uploadStatementSet } = useStatementUploader();

  // iter1230 - "Retry backlog fee" surface. Fetches upload jobs stuck at
  // `pending_payment` (declined charge) OR `awaiting_confirmation` (never
  // confirmed) per bank account so we can render a retry pill on the tile.
  // Injecting the job into BackgroundJobsContext also causes the global
  // UploadBacklogModal to auto-open on click.
  const [pendingJobsByAccount, setPendingJobsByAccount] = useState({});
  const refreshPendingUploadJobs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bank-feeds/upload-jobs/pending-payment`);
      const map = {};
      for (const j of (data?.jobs || [])) {
        if (!j.bank_account_id) continue;
        // Keep only the most recent per account (results are already sorted desc).
        if (!map[j.bank_account_id]) map[j.bank_account_id] = j;
      }
      setPendingJobsByAccount(map);
    } catch (_) { /* non-fatal */ }
  }, []);

  useEffect(() => {
    refreshPendingUploadJobs();
    const iv = setInterval(refreshPendingUploadJobs, 30000);
    return () => clearInterval(iv);
  }, [refreshPendingUploadJobs]);

  // Refresh when any context job flips through pending_payment/completion,
  // so a decline or a successful retry updates the pill without a reload.
  useEffect(() => {
    if (!backgroundJobsCtx?.jobs) return;
    const hasRelevantChange = backgroundJobsCtx.jobs.some(
      (j) => j.status === 'pending_payment' || j.status === 'completed' || j.status === 'failed'
    );
    if (hasRelevantChange) refreshPendingUploadJobs();
  }, [backgroundJobsCtx?.jobs, refreshPendingUploadJobs]);

  const handleRetryBacklogFee = (accountId) => {
    const job = pendingJobsByAccount[accountId];
    if (!job || !backgroundJobsCtx?.addJob) return;
    // iter1242 · The job may already be in the tracker (hydrated on
    // page load by /upload-jobs/active). If it is, don't `addJob`
    // (that appends a duplicate with wrong status). Just request the
    // review modal for the existing tracked entry.
    const alreadyTracked = (backgroundJobsCtx.jobs || []).some(
      (j) => j.jobId === job.job_id
    );
    if (!alreadyTracked) {
      backgroundJobsCtx.addJob({
        jobId: job.job_id,
        filename: job.filename,
        initialStatus: job.status,
        initialBilling: job.billing,
        initialBridgePreview: job.bridge_preview,
      });
    }
    // iter1238 Phase 8.9 — explicitly ask the global provider to open
    // the review modal for THIS job. Provider no longer auto-opens on
    // page load; opening is user-initiated.
    if (backgroundJobsCtx?.requestReviewOpen) {
      // Small delay so the addJob state flush lands first.
      setTimeout(() => backgroundJobsCtx.requestReviewOpen(job.job_id), 60);
    }
  };

  // iter1238 Phase 8.9 — Cancel-with-confirm flow for pending backlog
  // jobs. User-driven cancel from the Bank Account page pill, replaces
  // the auto-blocking modal behavior.
  const [cancelBacklogTarget, setCancelBacklogTarget] = useState(null); // { accountId, job }
  const [cancellingBacklog, setCancellingBacklog] = useState(false);
  const askCancelBacklog = (accountId) => {
    const job = pendingJobsByAccount[accountId];
    if (!job) return;
    setCancelBacklogTarget({ accountId, job });
  };
  const confirmCancelBacklog = async () => {
    if (!cancelBacklogTarget) return;
    const { accountId, job } = cancelBacklogTarget;
    setCancellingBacklog(true);
    try {
      await axios.post(`${API}/bank-feeds/upload-jobs/${job.job_id}/cancel`);
      // Remove from local pill state + global tracker
      setPendingJobsByAccount((prev) => {
        const next = { ...prev };
        delete next[accountId];
        return next;
      });
      if (backgroundJobsCtx?.removeJob) backgroundJobsCtx.removeJob(job.job_id);
      toast.success(`"${job.filename}" upload cancelled. You can re-upload the statement anytime.`);
      setCancelBacklogTarget(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not cancel this upload. Please try again.');
    } finally {
      setCancellingBacklog(false);
    }
  };
  // iter875 - Centralised data via BankFeedContext (dedupes the StrictMode
  // ×6 fan-out of config/connections endpoints reported by testing_agent
  // iter874). Local state mirrors are kept as a backwards-compatible
  // alias so the ~1.4k lines of rendering logic below don't need to be
  // rewritten.
  const {
    loading: ctxLoading,
    bankAccounts: ctxBankAccounts,
    monoConfig: ctxMonoConfig,
    monoConnections: ctxMonoConnections,
    plaidConfig: ctxPlaidConfig,
    plaidConnections: ctxPlaidConnections,
    stitchConfig: ctxStitchConfig,
    stitchConnections: ctxStitchConnections,
    refresh: ctxRefresh,
  } = useBankFeedData();

  const loading = ctxLoading;
  const bankAccounts = ctxBankAccounts;
  const monoConnections = ctxMonoConnections;
  const plaidConnections = ctxPlaidConnections;
  const stitchConnections = ctxStitchConnections;
  const monoConfig = ctxMonoConfig;
  const plaidConfig = ctxPlaidConfig;
  const stitchConfig = ctxStitchConfig;
  const [syncing, setSyncing] = useState({});
  const [uploadingFor, setUploadingFor] = useState(null);
  const [uploadBillingPreview, setUploadBillingPreview] = useState(null);
  const [confirmingUploadBilling, setConfirmingUploadBilling] = useState(false);
  // Backfill progress modal - opened on reconnect or via 'Catch up now' menu item
  const [backfillFor, setBackfillFor] = useState(null); // { bankAccountId, bankName } | null
  const [showAddBankDialog, setShowAddBankDialog] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  
  // Disconnect confirmation state
  const [disconnectTarget, setDisconnectTarget] = useState(null); // { connectionId, provider, bankName }
  const [disconnecting, setDisconnecting] = useState(false);

  // Statement vault state (Iter 804)
  const [statementUploadFor, setStatementUploadFor] = useState(null); // bankAccount | null
  // iter1381 · Starter modal for the async LLM-parser upload flow (guide +
  // limits + template downloads). Every per-account "Upload Statement" CTA
  // in this page (hover chip, 3-dot menu, HistoryGapBanner) opens this
  // starter first; its primary CTA then triggers the per-account hidden
  // file input `upload-${accountId}` which is what already wires into
  // handleStatementUpload -> useStatementUploader.
  const [uploadStarterAccountId, setUploadStarterAccountId] = useState(null);
  const openUploadStarterForAccount = useCallback((id) => setUploadStarterAccountId(id), []);
  // 9 Oct 2026 · "Bank connection didn't finish" fallback. useMonoConnect / the Plaid row launcher
  // persist the abandoned attempt in localStorage so the nudge survives leaving and coming back.
  const [connectFallback, setConnectFallback] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CONNECT_FALLBACK_KEY) || 'null'); } catch { return null; }
  });
  useEffect(() => {
    const onAbandoned = (e) => setConnectFallback(e.detail || null);
    window.addEventListener('bank-connect:abandoned', onAbandoned);
    return () => window.removeEventListener('bank-connect:abandoned', onAbandoned);
  }, []);
  const dismissConnectFallback = useCallback(() => {
    try { localStorage.removeItem(CONNECT_FALLBACK_KEY); } catch { /* no-op */ }
    setConnectFallback(null);
  }, []);
  const { unconfirmed: connectUnconfirmed, dismiss: dismissConnectUnconfirmed } = useConnectUnconfirmed();
  // iter1384 · Fill Gap chip state. Fetches on mount and after any
  // delete/upload success; a per-account amber chip opens the
  // PostDeleteGapFillModal pre-populated with the stored gap window.
  const [gapReminders, setGapReminders] = useState([]);
  const [openGapFillFor, setOpenGapFillFor] = useState(null); // reminder row | null
  const fetchGapReminders = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/bank-feeds/pending-gap-reminders');
      setGapReminders(Array.isArray(data?.reminders) ? data.reminders : []);
    } catch (_) {
      setGapReminders([]);
    }
  }, []);
  const [obDialogAccount, setObDialogAccount] = useState(null); // iter 990: opening balance prompt
  // iter1225 - "Enrich international transfer details" flow for Plaid/Mono
  // accounts that only surface the last-4 digits and/or lack SWIFT/IBAN.
  // Opens AddBankAccountModal in edit mode with the row pre-loaded.
  const [enrichingBank, setEnrichingBank] = useState(null);
  const [statementVaultFor, setStatementVaultFor] = useState(null);   // bankAccount | null
  
  // Open Banking Consent State
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [consentProvider, setConsentProvider] = useState('mono');
  const [pendingConnection, setPendingConnection] = useState(null);

  // Ledger/Transaction expansion state
  const [expandedAccount, setExpandedAccount] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // iter 909 - Plaid backlog fee state (parity with Mono backlog flow)
  const [plaidBacklog, setPlaidBacklog] = useState(null); // { connection_id, backlog_tx_count, backlog_fee, backlog_currency, symbol, institution_name } | null
  const [plaidBacklogConfirming, setPlaidBacklogConfirming] = useState(false);
  const [plaidBacklogPaymentFailure, setPlaidBacklogPaymentFailure] = useState(null);

  // Country-to-provider mapping
  // iter 908 - Plaid restricted to US-only in production (licence pending for
  // UK and EU). GB users see "Coming soon"; all other unsupported countries
  // see a "not available in your country" message. NG → Mono unchanged.
  const plaidCountries = ['US'];
  // GTM launch scope - Mono connect is Nigeria-only. GH/KE (and all other
  // African countries) are strictly manual banking until expansion.
  const monoCountries = ['NG'];
  // Stitch (ZA/TZ) parked - not configured in production; manual banking only.
  const stitchCountries = [];
  const comingSoonCountries = ['GB']; // UK. Plaid licence pending

  const isPlaidCountry = plaidCountries.includes(business?.country);
  const isMonoCountry = monoCountries.includes(business?.country);
  const isStitchCountry = stitchCountries.includes(business?.country);
  const isComingSoonCountry = comingSoonCountries.includes(business?.country);
  const hasBankConnection = isPlaidCountry || isMonoCountry || isStitchCountry;

  // Define fetchData BEFORE useMonoConnect to avoid temporal dead zone.
  // iter875 - Delegates to the BankFeedContext (centralised, in-flight
  // deduped). Returns the same Promise so callers can `await` it.
  const fetchData = useCallback(async () => {
    await ctxRefresh();
  }, [ctxRefresh]);

  // Unified Mono connect hook - staged flow
  const {
    connectBank: monoConnectBank,
    reconnectBank: monoReconnectBank,
    relinkBank: monoRelinkBank,
    confirmBacklog, dismissBacklog, rollbackConnection,
    connecting, confirmingBacklog,
    syncStatus, syncMessage, backlogInfo, resetState,
    preflightData, preflightLoading
  } = useMonoConnect({ onSuccess: fetchData, user });

  // Auto-detect any bank with active background processing
  const [processingBankId, setProcessingBankId] = useState(null);
  const [processingBankName, setProcessingBankName] = useState('');

  // Tracks the Plaid / Stitch redirect handshake. (Mono uses the hook's own
  // `connecting`.) Without this, calls to setConnecting() throw ReferenceError
  // and silently break the Connect-via-Plaid / Connect-via-Stitch flows.
  const [providerConnecting, setProviderConnecting] = useState(false);
  // iter1121 - persistent progress splash between "Plaid Link closes" and
  // "backlog dialog / success toast fires". Fills the 3-15s dead zone where
  // users used to panic and click Connect again.
  // Shape: null | { phase: 'exchanging' | 'polling' | 'finishing', institutionName?: string }
  const [plaidConnectProgress, setPlaidConnectProgress] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const checkActive = async () => {
      try {
        const res = await axios.get(`${API}/bank-accounts/active-processing`);
        if (cancelled) return;
        const accounts = res.data?.accounts || [];
        if (accounts.length > 0) {
          setProcessingBankId(accounts[0].id);
          setProcessingBankName(accounts[0].bank_name || '');
        } else {
          setProcessingBankId(null);
        }
      } catch (e) { /* silent */ }
    };
    checkActive();
    const interval = setInterval(checkActive, 10000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // iter1384 · fetch open Fill Gap reminders on mount + every 30s (so a
  // successful upload/OB replay that auto-clears them makes the chip
  // disappear without a full page refresh).
  useEffect(() => {
    fetchGapReminders();
    const t = setInterval(fetchGapReminders, 30000);
    return () => clearInterval(t);
  }, [fetchGapReminders]);

  // iter875 - Initial fetch is owned by BankFeedProvider on mount; no
  // need for a redundant fetchData() effect here. fetchData() now
  // delegates to ctxRefresh() with force=true so it's reserved for
  // explicit user-driven refreshes (e.g., after connect/reconnect).

  // iter-bugfix (polling-ceiling-removal): re-hydrate any unpaid Plaid
  // backlog dialogs on mount, so users who closed the connect-wizard
  // before paying still see the prompt the next time they open My Banks.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/plaid/pending-backlogs`);
        if (cancelled) return;
        const pending = res.data?.pending_backlogs || [];
        if (pending.length > 0 && !plaidBacklog) {
          // Local state kept for BankConnectProgress gating; the modal
          // itself is rendered by GlobalBacklogModalProvider (iter1207).
          setPlaidBacklog(pending[0]);
          triggerBacklogRefresh();
        }
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // iter1204-plaid-ux: pick up the sync handoff from PlaidOAuthResumePage.
  // When the OAuth resume page completes `/plaid/exchange-token`, it
  // navigates here with `?plaid_syncing_name=<inst>&plaid_syncing_connection=<id>`
  // so we can immediately open BankConnectProgress in the `polling` phase
  // and start polling sync-status — no visual gap between "user completes
  // Plaid" and "user sees their bank importing".
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const syncName = params.get('plaid_syncing_name');
    const syncConn = params.get('plaid_syncing_connection');
    if (!syncName && !syncConn) return;
    setPlaidConnectProgress({
      phase: 'polling',
      institutionName: syncName || 'your bank',
    });
    if (syncConn) {
      pollPlaidSyncStatus(syncConn);
    } else {
      // No connection_id (rare) — still show progress for a beat, then
      // clear so the user can see the connected bank tile.
      setTimeout(() => setPlaidConnectProgress(null), 4000);
    }
    // Strip the params so a refresh doesn't re-trigger the splash.
    try {
      const url = new URL(window.location.href);
      ['plaid_syncing_name', 'plaid_syncing_connection', 'plaid_relaunch'].forEach(
        (k) => url.searchParams.delete(k)
      );
      window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
    } catch (_) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle return from Mono's hosted reauth/connect page (full-page redirect).
  // Mono STRIPS our custom query params and adds its own:
  //   success: ?status=linked&reason=account_linked (or reauth_completed)
  //   error  : ?status=error&reason=...
  //   exit   : ?status=closed | ?status=cancelled
  // The bank_account_id is read from sessionStorage (set by reconnectBank()).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const monoStatus = params.get('status');
    const monoReason = params.get('reason');
    const ourReauth = params.get('reauth'); // legacy fallback

    const hasMonoCallback = monoStatus === 'linked' || monoStatus === 'successful' || ourReauth === 'success';
    const hasMonoError = monoStatus === 'error' || monoStatus === 'failed' || ourReauth === 'error';
    const hasMonoExit = monoStatus === 'closed' || monoStatus === 'cancelled' || monoStatus === 'exited';
    if (!hasMonoCallback && !hasMonoError && !hasMonoExit) { settleAbandonedMonoReturn(); return; }

    // Pull bank_account_id from sessionStorage (set by reconnectBank/relinkBank before redirect)
    let baId = null;
    let isRelink = false;
    try {
      baId = sessionStorage.getItem('mono_reauth_bank_account_id');
      sessionStorage.removeItem('mono_reauth_bank_account_id');
      // Fallback to the relink key - used by the "Re-link bank (force fresh sync)" CTA
      if (!baId) {
        baId = sessionStorage.getItem('mono_relink_bank_account_id');
        if (baId) isRelink = true;
      }
      sessionStorage.removeItem('mono_relink_bank_account_id');
      sessionStorage.removeItem('mono_relink_ref');
      sessionStorage.removeItem('mono_redirect_started_at');
    } catch (_) { /* ignore */ }
    // Legacy fallback - older flow passed bank_account_id in the URL
    if (!baId) baId = params.get('bank_account_id');

    if (hasMonoCallback) {
      clearConnectUnconfirmed(baId);
      // iter-bugfix (G-5): persist a "catching up after reconnect" marker so
      // MonoBanksRefreshSection can keep showing the status even if the user
      // navigates away from /banks. Cleared automatically when fresh
      // transactions land (last_fresh_fetch_at > marker) or after 15 min.
      try {
        if (baId) {
          const markers = JSON.parse(localStorage.getItem('mono_reauth_markers') || '{}');
          markers[baId] = new Date().toISOString();
          localStorage.setItem('mono_reauth_markers', JSON.stringify(markers));
        }
      } catch (_) { /* ignore */ }

      toast.success(
        isRelink
          ? 'Bank re-linked: pulling fresh historical transactions, this may take several minutes…'
          : 'Bank reconnected. Catching up your transactions…',
        { duration: 8000 }
      );

      // iter971k-Z · Turn 5k - GAP #A: server-side reauth confirmation.
      // Mono's `account_reauthorized` webhook is best-effort. If delivery
      // fails after all retries we'd never fetch - bank sits session_expired
      // forever. Calling this endpoint from the browser is an ALWAYS-ON
      // fallback that verifies the reauth with Mono directly and kicks
      // the orchestrator ourselves. Idempotent - safe when the webhook
      // also fires (debounced by the orchestrator's phase-check).
      if (baId && !isRelink) {
        try {
          apiClient.post(
            '/open-banking-feeds/mono/confirm-reauth-completed',
            { bank_account_id: baId }
          ).then((r) => {
            if (r?.data?.status === 'account_mismatch') {
              toast.error(r.data.message || 'Your bank login connected a different account. Please reconnect and pick the correct account.', { duration: 15000 });
            }
          }).catch((e) => {
            // Non-fatal - the webhook path is still expected to work.
            console.debug('[confirm-reauth-completed] non-fatal error:', e?.response?.status);
          });
        } catch (_) { /* ignore */ }
      }

      // Open the backfill modal once we know which bank to track.
      const openBackfill = (ba) => {
        const acc = (bankAccounts || []).find(b => b.id === ba);
        setBackfillFor({ bankAccountId: ba, bankName: acc?.bank_name || 'Bank' });
      };
      if (baId) {
        if (bankAccounts && bankAccounts.length > 0) {
          openBackfill(baId);
        } else {
          // bankAccounts not loaded yet; wait one tick
          setTimeout(() => openBackfill(baId), 1200);
        }
      }
      // Trigger a fresh data fetch so the new active state is visible
      fetchData();
    } else if (hasMonoError) {
      recordConnectUnconfirmed(baId, 'reauth');
      const msg = monoReason
        ? `Bank reconnection failed (${monoReason}). Please try again.`
        : 'Bank reconnection failed. Please try again.';
      toast.error(msg, { duration: 8000 });
    } else if (hasMonoExit) {
      recordConnectUnconfirmed(baId, 'reauth');
      toast.info('Bank reconnection was cancelled. You can try again any time.', { duration: 5000 });
    }

    // Strip Mono's callback params from the URL so refreshes don't re-trigger.
    try {
      const url = new URL(window.location.href);
      ['status', 'reason', 'code', 'state', 'reauth', 'bank_account_id'].forEach(k => url.searchParams.delete(k));
      window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
    } catch (_) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankAccounts.length]);

  const handlePlaidSuccess = async (publicToken, metadata) => {
    setProviderConnecting(true);
    // iter1121 - show blocking progress splash from the moment Plaid Link
    // closes, so the 3-15s "gap" doesn't feel like the app broke.
    const institutionName = metadata?.institution?.name;
    setPlaidConnectProgress({ phase: 'exchanging', institutionName });
    try {
      const exchangeRes = await axios.post(`${API}/plaid/exchange-token`, {
        public_token: publicToken,
        institution_id: metadata?.institution?.institution_id,
        institution_name: institutionName
      });
      toast.success(`Connected to ${institutionName || 'your bank'}: importing transactions now`);
      fetchData();
      // iter 909 - Poll backlog/sync status so the user is prompted to pay
      // the historical-transactions fee if their accounting start date is
      // before the current month.
      const connId = exchangeRes?.data?.connection_id;
      if (connId) {
        // Advance the splash into the "fetching" phase — pollPlaidSyncStatus
        // will dismiss the splash once it hits backlog_required (BacklogConfirmDialog
        // takes over) or any other terminal state.
        setPlaidConnectProgress({ phase: 'polling', institutionName });
        pollPlaidSyncStatus(connId);
      } else {
        // No connection_id — nothing to poll. Close the splash.
        setPlaidConnectProgress(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect bank account');
      setPlaidConnectProgress(null);
    } finally {
      setProviderConnecting(false);
    }
  };

  // iter-bugfix (polling-ceiling-removal): Poll Plaid sync-status until we hit
  // a terminal state. NO time-based cutoff - the backend (webhooks +
  // plaid_sync_scheduler sweeper) drives state, and the user may have a
  // multi-thousand-tx history that Plaid takes 30+ minutes to deliver.
  // We bail if the page unmounts (cancelledRef) or the user signals close.
  const pollPlaidSyncStatus = useCallback(async (connectionId) => {
    const TERMINAL = new Set([
      'no_backlog', 'backlog_charged', 'complete', 'completed', 'failed',
      'transactions_unavailable',
    ]);
    let pollCount = 0;
    while (pollCount < Number.MAX_SAFE_INTEGER) {
      pollCount += 1;
      try {
        const res = await axios.get(`${API}/plaid/sync-status/${connectionId}`);
        const s = res.data?.sync_status;
        if (s === 'backlog_required') {
          setPlaidConnectProgress(null); // dialog takes over
          setPlaidBacklog({
            connection_id: connectionId,
            backlog_tx_count: res.data.backlog_tx_count,
            backlog_fee: res.data.backlog_fee,
            backlog_currency: res.data.backlog_currency,
            symbol: res.data.symbol,
            institution_name: res.data.institution_name,
            backlog_message: res.data.backlog_message,
            backlog_fee_waived: res.data.backlog_fee_waived,
            provider_minimum: res.data.provider_minimum,
          });
          // iter1207 - dialog is rendered by GlobalBacklogModalProvider;
          // nudge it to poll immediately so there's no 60s gap.
          triggerBacklogRefresh();
          return;
        }
        if (TERMINAL.has(s)) {
          setPlaidConnectProgress(null); // hide the splash on terminal state
          fetchData();
          return;
        }
      } catch (_) { /* keep polling */ }
      // iter1121 - after ~20s in polling phase, advance the splash to
      // "finishing" so the user sees continuous progress signalling; after
      // ~2min close the splash entirely (the global backlog banner will
      // continue to reflect progress).
      if (pollCount === 8) {
        setPlaidConnectProgress((prev) => prev ? { ...prev, phase: 'finishing' } : prev);
      }
      // Soft hint after 2 min - non-blocking, just a toast once.
      if (pollCount === 48) {
        setPlaidConnectProgress(null);
        toast.info(
          'Your bank is still backfilling transactions: this can take up to ' +
          '30 minutes. You can navigate away; we will continue syncing in the ' +
          'background.',
          { duration: 8000 }
        );
      }
      await new Promise(r => setTimeout(r, 2500));
    }
  }, [fetchData]);

  // iter 909 - User confirms paying the Plaid backlog fee
  const confirmPlaidBacklog = useCallback(async () => {
    if (!plaidBacklog) return;
    setPlaidBacklogConfirming(true);
    setPlaidBacklogPaymentFailure(null);
    try {
      const res = await axios.post(
        `${API}/plaid/confirm-backlog/${plaidBacklog.connection_id}`,
        {}
      );
      if (res.data?.success && res.data?.payment_succeeded) {
        // iter1119 - fee waived if below provider minimum (Stripe $0.50 USD /
        // Paystack ₦100). Show a "no charge" message instead of a $0.00 charge.
        // iter1204 - posting is now async (Celery). transactions_posted is
        // null when processing_in_background=true; show a "processing" toast.
        const processingAsync = res.data?.processing_in_background === true;
        if (res.data?.waived) {
          toast.success(
            processingAsync
              ? "No charge (fee below provider minimum). Historical transactions are posting in the background — check the Bank Feed shortly."
              : `Historical transactions posted (${res.data.transactions_posted}). No charge — fee was below the payment processor minimum.`
          );
        } else {
          toast.success(
            processingAsync
              ? `${plaidBacklog.symbol}${(plaidBacklog.backlog_fee || 0).toFixed(2)} charged. Historical transactions are posting in the background — check the Bank Feed shortly.`
              : `Historical transactions posted (${res.data.transactions_posted}). ${plaidBacklog.symbol}${(plaidBacklog.backlog_fee || 0).toFixed(2)} charged.`
          );
        }
        setPlaidBacklog(null);
        fetchData();
      } else if (res.data?.already_settled) {
        setPlaidBacklog(null);
        fetchData();
      } else if (res.data?.payment_failed) {
        setPlaidBacklogPaymentFailure({
          reason: res.data.reason,
          total_fee: res.data.total_fee,
          currency: res.data.currency,
          backlog_tx_count: res.data.backlog_tx_count,
          can_retry: res.data.can_retry,
        });
      } else if (res.data?.posting_error) {
        toast.warning('Payment succeeded but transaction posting hit an error. Support notified.');
        setPlaidBacklog(null);
        fetchData();
      }
    } catch (err) {
      setPlaidBacklogPaymentFailure({
        reason: err.response?.data?.detail || 'Unexpected error during payment',
        can_retry: true,
      });
    } finally {
      setPlaidBacklogConfirming(false);
    }
  }, [plaidBacklog, fetchData]);

  const declinePlaidBacklog = useCallback(() => {
    // User declined - leave connection in `backlog_required` state. They can
    // pay later by reconnecting or via Settings → Banking.
    toast.info('Bank stays connected. Historical transactions will appear after you pay the backlog fee.');
    setPlaidBacklog(null);
  }, []);

  const handleStitchConnect = async () => {
    // Show consent dialog first
    setConsentProvider('stitch');
    setPendingConnection('stitch');
    setShowConsentDialog(true);
  };

  const proceedWithStitchConnect = async () => {
    setProviderConnecting(true);
    try {
      const response = await axios.post(`${API}/stitch/authorize-url`);
      // Redirect to Stitch authorization
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start bank connection');
      setProviderConnecting(false);
    }
  };

  // Handle Mono connection with consent
  const handleMonoConnect = async () => {
    // Feb 2026 - defence in depth after a US pilot user was routed
    // into the Mono flow when `/api/plaid/config` had not yet resolved.
    // The bank connection provider must be pinned to the business's
    // country, never to whichever provider config happens to have
    // loaded first. Same guard on `handlePlaidConnect` below.
    if (isPlaidCountry) {
      toast.error('This is a US business. Use "Connect US bank or card".');
      return;
    }
    if (isStitchCountry) {
      toast.error('This region uses Stitch. Please use the correct connect button.');
      return;
    }
    if (!isMonoCountry) {
      toast.error('Bank connection is not yet available in your country.');
      return;
    }
    const ok = await requireStepUp({ reason: 'Connect a bank account to AkountFly' });
    if (!ok) return;
    setConsentProvider('mono');
    setPendingConnection('mono');
    setShowConsentDialog(true);
  };

  // 9 Oct 2026 · shared by the 3-dot menu item AND the on-card action chip.
  const handleMonoUpdate = useCallback(async (accountId) => {

        // iter971k-Z · Turn 5i - direct fetch first.
        // /mono/refresh-now pulls new transactions in
        // place when the connection is still healthy.
        // Only when the backend EXPLICITLY reports
        // reauth_required (a proper 4xx API response)
        // do we fall through to the reauth URL flow.
        // Network errors / 5xx do NOT burn reauth.
        try {
          let data;
          let refreshNowNetworkError = false;
          try {
            const res = await apiClient.post(
              `/open-banking-feeds/mono/refresh-now`,
              { bank_account_id: accountId }
            );
            data = res.data;
          } catch (e) {
            const s = e?.response?.status;
            if (s && s >= 400 && s < 500 && s !== 404 && s !== 408) {
              data = { status: 'reauth_required', _fallback_from_refresh_now_error: s };
            } else {
              refreshNowNetworkError = true;
              data = { _error_status: s || 'network' };
            }
          }
          if (refreshNowNetworkError) {
            toast.error(
              data._error_status && data._error_status >= 500
                ? 'Bank refresh service is temporarily unavailable: please try again in a moment.'
                : 'Could not reach the refresh service. Check your connection and try again.'
            );
            return;
          }
          if (data.status === 'queued') {
            toast.success('Fetching latest transactions: we’ll notify you when done.', { duration: 4000 });
            try {
              window.dispatchEvent(new CustomEvent('mono:refresh-triggered', {
                detail: { bank_account_id: accountId },
              }));
            } catch (_) { /* ignore */ }
            return;
          }
          if (data.status === 'session_cooldown') {
            toast.info(data.message || `Just refreshed: try again in ${data.retry_after_seconds || 60}s.`);
            return;
          }
          if (data.status === 'quota_exhausted') {
            toast.warning(data.message || 'You’ve used today’s refreshes for this bank. Try again tomorrow.');
            return;
          }
          // reauth_required → request-update
          const { data: reauth } = await apiClient.post(
            `/open-banking-feeds/mono/nigeria/request-update`,
            { bank_account_id: accountId }
          );
          if (reauth.status === 'rate_limited') {
            toast.warning(reauth.message || "You've already used today's update for this bank. Come back tomorrow.");
            return;
          }
          if (reauth.status === 'ready' && reauth.reauth_url) {
            if (reauth.flow === 'relink_picker') {
              toast.info(
                reauth.expected_account_last4
                  ? `Your bank will show its account list - pick the account ending ${reauth.expected_account_last4}.`
                  : "Your bank will show its account list - pick the same account this feed is connected to.",
                { duration: 8000 }
              );
              try {
                markMonoRedirect('relink', accountId, reauth.ref);
              } catch (_) { /* ignore */ }
            } else {
              toast.info("Opening your bank's secure update page. If your bank session is still valid this completes automatically: otherwise you'll be asked to log in.", { duration: 5000 });
              try {
                markMonoRedirect('reauth', accountId);
              } catch (_) { /* ignore */ }
            }
            window.location.assign(reauth.reauth_url);
            return;
          }
          toast.error('Could not start the update. Please try again in a moment.');
        } catch (e) {
          const detail = e?.response?.data?.detail;
          toast.error(detail || 'Could not start the update: please try again.');
        }
  }, []);

  const proceedWithMonoConnect = (bankAccountId = null) => {
    monoConnectBank(monoConfig.public_key, bankAccountId);
  };

  // Handle Plaid connection with consent
  const handlePlaidConnect = async () => {
    if (!isPlaidCountry) {
      toast.error('This connect flow is US-only. Please use the correct connect button for your country.');
      return;
    }
    const ok = await requireStepUp({ reason: 'Connect a bank account to AkountFly' });
    if (!ok) return;
    setConsentProvider('plaid');
    setPendingConnection('plaid');
    setShowConsentDialog(true);
  };

  // After consent is given, proceed with connection
  const handleConsentGiven = () => {
    if (pendingConnection === 'stitch') {
      proceedWithStitchConnect();
    } else if (pendingConnection === 'mono') {
      // Pass the pending bank account id if one was set (for per-account linking)
      const bankAccountId = window.__pendingBankAccountId || null;
      window.__pendingBankAccountId = null;
      proceedWithMonoConnect(bankAccountId);
    }
    // For Plaid, the PlaidLinkButton handles its own flow
    setPendingConnection(null);
  };

  const toggleLedger = async (accountId) => {
    if (expandedAccount === accountId) {
      setExpandedAccount(null);
      setLedgerData(null);
      return;
    }
    setExpandedAccount(accountId);
    setLedgerLoading(true);
    try {
      const res = await axios.get(`${API}/bank-accounts/${accountId}/ledger`);
      setLedgerData(res.data);
    } catch (err) {
      toast.error('Failed to load transactions');
      setExpandedAccount(null);
    } finally {
      setLedgerLoading(false);
    }
  };

  const syncConnection = async (connectionId, provider) => {
    setSyncing(prev => ({ ...prev, [connectionId]: true }));
    try {
      let endpoint = `${API}/mono/connections/${connectionId}/sync`;
      if (provider === 'plaid') {
        endpoint = `${API}/plaid/connections/${connectionId}/sync`;
      } else if (provider === 'stitch') {
        endpoint = `${API}/stitch/connections/${connectionId}/sync`;
      }
      const response = await axios.post(endpoint);
      toast.success(`Synced ${response.data.synced || response.data.added || 0} new transactions`);
      if ((response.data.synced || response.data.added) > 0) {
        toast.info('New transactions need categorization', {
          action: { label: 'Review', onClick: () => navigate('/transactions/review') }
        });
      }
    } catch (error) {
      toast.error('Failed to sync transactions');
    } finally {
      setSyncing(prev => ({ ...prev, [connectionId]: false }));
    }
  };

  // iter 950 - deep-link support: /banks?refresh=<bank_account_id> auto-fires
  // a sync on that account so the "Refresh Now" CTA in the stalled-bank
  // digest email actually does something. One-shot: strip the param after
  // firing so a page refresh doesn't re-trigger.
  const refreshParamHandledRef = useRef(false);
  useEffect(() => {
    const target = searchParams.get('refresh');
    if (!target || loading || refreshParamHandledRef.current) return;

    const mono = monoConnections.find(c => c.bank_account_id === target);
    const plaid = plaidConnections.find(c => c.bank_account_id === target);
    const stitch = stitchConnections.find(c => c.bank_account_id === target);
    const conn = mono || plaid || stitch;
    if (!conn) return;

    refreshParamHandledRef.current = true;
    const provider = mono ? 'mono' : plaid ? 'plaid' : 'stitch';
    toast.info('Refreshing bank data from your email link…');
    syncConnection(conn.id, provider);

    // Strip ?refresh= so the sync doesn't fire again on route re-render
    const next = new URLSearchParams(searchParams);
    next.delete('refresh');
    setSearchParams(next, { replace: true });
  }, [loading, monoConnections, plaidConnections, stitchConnections, searchParams]);

  // Get the correct connectionId for disconnect/sync operations
  const getConnectionId = (account) => {
    // If connectionId was pre-attached during allAccounts merge, use it directly
    if (account.connectionId) return account.connectionId;
    // Fallback: look up in connection arrays
    if (account.open_banking_connected || account.source === 'mono') {
      const match = monoConnections.find(c => c.bank_account_id === account.id);
      if (match) return match.id;
    }
    if (account.source === 'plaid') {
      const match = plaidConnections.find(c => c.bank_account_id === account.id);
      if (match) return match.id;
    }
    if (account.source === 'stitch') {
      const match = stitchConnections.find(c => c.bank_account_id === account.id);
      if (match) return match.id;
    }
    return account.id;
  };

  // Show disconnect warning dialog
  const promptDisconnect = (account) => {
    const connectionId = getConnectionId(account);
    const provider = account.open_banking_source || account.source || 'mono';
    const bankName = account.institution_name || account.bank_name || 'this bank';
    // iter1108 - enumerate sibling sub-accounts under the same Plaid Item
    // so the confirmation modal is honest about scope. Plaid Items are
    // atomic - one revocation kills every sub-account under this login.
    const siblings = getSubAccountSiblings(account);
    setDisconnectTarget({
      connectionId,
      provider,
      bankName,
      subAccounts: siblings || [account],
    });
  };

  const confirmDisconnect = async () => {
    if (!disconnectTarget) return;
    setDisconnecting(true);
    try {
      const { connectionId, provider } = disconnectTarget;
      let endpoint = `${API}/mono/connections/${connectionId}`;
      if (provider === 'plaid') {
        endpoint = `${API}/plaid/connections/${connectionId}`;
      } else if (provider === 'stitch') {
        endpoint = `${API}/stitch/connections/${connectionId}`;
      }
      await axios.delete(endpoint);
      toast.success('Bank disconnected. Your existing transactions and GL accounts are preserved.');
      setDisconnectTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect account');
    } finally {
      setDisconnecting(false);
    }
  };

  // iter1108 - open Plaid Link update-mode with Account Select so the user
  // can add or remove sub-accounts under an existing connection without a
  // full disconnect. Plaid does NOT support removing individual accounts
  // via API - update mode is the only path (verified via Plaid docs).
  const [manageAccountsFor, setManageAccountsFor] = useState(null); // {connectionId, linkToken, institution}
  const [manageIntent, setManageIntent] = useState(null); // pre-confirm state {account, institution, siblingCount}
  const [managingAccounts, setManagingAccounts] = useState(false);

  // iter1112 - Ad-hoc Plaid Link launcher fired from the 3-dots menu on
  // manual or reauth-needed rows. Holds a link_token + mode so a single
  // <PlaidRowLauncher> instance handles both flows (fresh connect via
  // /plaid/create-link-token and reauth via /plaid/update-link-token).
  //   { linkToken, mode: 'connect' | 'reauth', accountId, connectionId? }
  const [plaidRowLink, setPlaidRowLink] = useState(null);
  const [plaidRowLoading, setPlaidRowLoading] = useState(null);

  const startPlaidRowConnect = useCallback(async (account) => {
    const accountId = account.id;
    setPlaidRowLoading(accountId);
    try {
      const res = await axios.post(`${API}/plaid/create-link-token`);
      const linkToken = res.data?.link_token;
      if (!linkToken) throw new Error('No link_token returned');
      // iter1115 - Persist link_token so /oauth/plaid resume can recover it
      // when the user picks an OAuth bank (Revolut US, Chase, BofA, etc.).
      // Without this, Revolut reconnects silently dropped every sub-account
      // back to "manual" because exchange-token never fired.
      savePlaidLinkToken(linkToken, window.location.pathname);
      // Hint the backend that this Link session is initiated FROM a
      // specific manual row. Backend orphan matcher (iter1112) will still
      // merge by (institution, mask); the sessionStorage hint is used
      // only for post-success telemetry + backfill modal targeting.
      try {
        sessionStorage.setItem('akf_plaid_row_hint', JSON.stringify({
          bank_account_id: accountId,
          initiated_at: new Date().toISOString(),
        }));
      } catch { /* no-op */ }
      setPlaidRowLink({ linkToken, mode: 'connect', accountId });
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not start bank sync';
      toast.error(detail);
    } finally {
      setPlaidRowLoading(null);
    }
  }, []);

  const startPlaidRowReauth = useCallback(async (account) => {
    const accountId = account.id;
    const connectionId = getConnectionId(account);
    if (!connectionId) {
      toast.error('Could not resolve the bank connection for reauth.');
      return;
    }
    setPlaidRowLoading(accountId);
    try {
      const res = await axios.post(`${API}/plaid/update-link-token`, {
        connection_id: connectionId,
      });
      const linkToken = res.data?.link_token;
      if (!linkToken) throw new Error('No link_token returned');
      // iter1115 - Same OAuth resume persistence as fresh connect.
      savePlaidLinkToken(linkToken, window.location.pathname);
      setPlaidRowLink({ linkToken, mode: 'reauth', accountId, connectionId });
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not start reconnect';
      toast.error(detail);
    } finally {
      setPlaidRowLoading(null);
    }
  }, []);

  const onPlaidRowSuccess = useCallback(async (publicToken, metadata) => {
    if (!plaidRowLink) return;
    try {
      if (plaidRowLink.mode === 'reauth') {
        // iter1121 - show the same splash for reconnect; it flashes briefly
        // but keeps the user from panic-clicking during the mark-reconnected
        // round-trip on slow networks.
        setPlaidConnectProgress({ phase: 'exchanging', institutionName: metadata?.institution?.name });
        await axios.post(`${API}/plaid/connections/${plaidRowLink.connectionId}/mark-reconnected`);
        toast.success('Reconnected: syncing now');
        setPlaidConnectProgress(null);
      } else {
        // Fresh connect - exchange the public token. Backend iter1112
        // orphan matcher merges the target manual row automatically
        // by (institution, mask) so the user's opening balance +
        // history stays attached.
        await handlePlaidSuccess(publicToken, metadata);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Bank sync setup ran into a problem.';
      toast.error(detail);
      setPlaidConnectProgress(null);
    } finally {
      setPlaidRowLink(null);
      try { sessionStorage.removeItem('akf_plaid_row_hint'); } catch { /* no-op */ }
      fetchData();
    }
  }, [plaidRowLink, fetchData, handlePlaidSuccess]);

  const openManageSubAccounts = useCallback((account) => {
    if (!account) return;
    const connectionId = getConnectionId(account);
    if (!connectionId) {
      toast.error('Could not resolve the bank connection for this account.');
      return;
    }
    const siblings = getSubAccountSiblings(account);
    setManageIntent({
      account,
      connectionId,
      institution: account.institution_name || account.bank_name || 'this bank',
      siblingCount: siblings ? siblings.length : 1,
    });
    // iter1112 - `subAccountGroups` is declared later in the render body
    // (memoised sub-account groupings). Referencing it here in the deps
    // triggers a TDZ ReferenceError on mount. `getConnectionId` and
    // `getSubAccountSiblings` are re-declared every render and always
    // close over the latest `subAccountGroups` value, so the callback
    // stays correct without listing it explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monoConnections, plaidConnections, stitchConnections]);

  const confirmAndLaunchManageAccounts = useCallback(async () => {
    if (!manageIntent) return;
    setManagingAccounts(true);
    try {
      const res = await axios.post(
        `${API}/plaid/connections/${manageIntent.connectionId}/manage-link-token`,
      );
      const linkToken = res.data?.link_token;
      if (!linkToken) throw new Error('No link_token returned');
      setManageAccountsFor({
        connectionId: manageIntent.connectionId,
        linkToken,
        institution: manageIntent.institution,
      });
      setManageIntent(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open account manager.');
    } finally {
      setManagingAccounts(false);
    }
  }, [manageIntent]);

  const onManageAccountsSuccess = useCallback(async () => {
    if (!manageAccountsFor) return;
    try {
      const res = await axios.post(
        `${API}/plaid/connections/${manageAccountsFor.connectionId}/sync-accounts`,
      );
      const added = res.data?.added_count || 0;
      const removed = res.data?.removed_count || 0;
      if (added || removed) {
        toast.success(
          `Sub-accounts updated: ${added ? `${added} added` : ''}${added && removed ? ', ' : ''}${removed ? `${removed} disconnected` : ''}.`
        );
      } else {
        toast.info('No changes to your sub-accounts.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not sync sub-accounts.');
    } finally {
      setManageAccountsFor(null);
      fetchData();
    }
  }, [manageAccountsFor, fetchData]);


  const handleStatementUpload = async (e, bankAccountId) => {
    const files = Array.from(e.target.files || []);
    const file = files[0];
    if (!file) return;

    // Validate file type
    const validExtensions = ['.csv', '.txt', '.pdf', '.xlsx', '.xls', '.ofx', '.qfx'];
    const hasValidExtension = files.every((f) => validExtensions.some(ext => f.name.toLowerCase().endsWith(ext)));
    if (!hasValidExtension) {
      toast.error('Unsupported file type. Please upload a CSV, PDF, Excel, OFX, or QFX file.');
      e.target.value = '';
      return;
    }

    try {
      toast.loading(files.length > 1 ? `Uploading ${files.length} statements...` : 'Uploading statement...', { id: 'upload-toast' });
      // iter1236 - shared upload helper. Owns the multipart POST,
      // BackgroundJobsContext registration, and typed error surfaces so
      // MyBanks and BankFeedCenter can't drift again.
      const { filename } = await uploadStatementSet(files, { bankAccountId });
      toast.dismiss('upload-toast');
      toast.success(files.length > 1
        ? `${files.length} statements are being read together — they will be joined and checked for gaps before anything is imported. Redirecting...`
        : `"${filename}" is being processed. Redirecting to Bank Transactions Center...`, { duration: 3000 });
      navigate('/bank-feeds');
    } catch (err) {
      toast.dismiss('upload-toast');
      if (err?.name === 'StatementAuthRefreshError') {
        toast.info('Your session was refreshed. Please click Upload again.');
      } else if (err?.name === 'StatementValidationMismatchError') {
        toast.error(err.mismatchReason || 'Statement validation failed');
      } else {
        // iter1443 R8 · Use the normalized userMessage from the hook so
        // structured `{error_code, message}` payloads (504 / 502) don't
        // render as "[object Object]".
        const detail = err?.userMessage
          || err?.response?.data?.detail
          || 'Failed to upload statement';
        const message = (typeof detail === 'string' && detail)
          ? detail
          : (detail?.message || 'Failed to upload statement');
        toast.error(message);
      }
    }
    e.target.value = '';
    setUploadingFor(null);
  };

  const handleConfirmUploadBilling = async () => {
    if (!uploadBillingPreview?.job_id) return;
    setConfirmingUploadBilling(true);
    try {
      const result = await axios.post(`${API}/bank-feeds/confirm-upload-billing`, {
        job_id: uploadBillingPreview.job_id
      });
      setUploadBillingPreview(null);
      toast.success(result.data.message || 'Payment confirmed. Processing transactions...');
      navigate('/bank-feeds');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Payment failed. Please try again.';
      toast.error(detail);
    } finally {
      setConfirmingUploadBilling(false);
    }
  };

  const handleDeclineUploadBilling = () => {
    setUploadBillingPreview(null);
    toast.info('Upload saved. You can confirm billing later.');
  };

  const deleteBankAccount = async (accountId) => {
    // iter1113 - Delete now returns 409 has_journal_history for any
    // account with posted entries. Surface as an actionable toast that
    // instructs the user to archive; the Archive item lives in the same
    // 3-dots menu so we don't need to auto-open the dialog.
    try {
      await axios.delete(`${API}/bank-accounts/${accountId}`);
      toast.success('Bank account deleted');
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && detail?.error_code === 'has_journal_history') {
        toast.error(
          detail.message ||
          'This account has posted journal entries and cannot be deleted. Archive it instead - the option is available in the same 3-dots menu.'
        );
      } else {
        toast.error(error?.normalised?.message || 'Failed to delete bank account');
      }
    }
  };

  // iter1113 - Archive is a sensitive action restricted to Settings > Banking.
  // The Banks page no longer surfaces Archive at all. Delete falls back to a
  // 409 error toast that points the user to Settings.

  const reactivateBankAccount = async (accountId) => {
    try {
      const res = await axios.post(`${API}/bank-accounts/${accountId}/reactivate`);
      const gap = res?.data?.gap;
      if (gap?.gap_days) {
        toast.success(
          `Reactivated as manual. ${gap.gap_days} day${gap.gap_days !== 1 ? 's' : ''} gap detected (${gap.gap_start} to ${gap.gap_end}). Reconnect or upload a statement.`
        );
      } else {
        toast.success('Bank reactivated as a manual account.');
      }
      fetchData();
      return res?.data || null;
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.normalised?.message || 'Failed to reactivate bank account');
      return null;
    }
  };

  // Sprint 1 F1.5 - controlled state for ConfirmDestructive dialog
  const [pendingDeleteAccount, setPendingDeleteAccount] = useState(null);

  const handleCreateTransfer = async (transferData) => {
    try {
      const response = await axios.post(`${API}/bank-transfer`, transferData);
      if (response.data.id) {
        toast.success(response.data.message);
        setShowTransferModal(false);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    }
  };


  // Combine all accounts for display - deduplicate connected banks
  // Connected bank accounts appear in both bankAccounts (with open_banking_connected)
  // AND monoConnections. We keep the bankAccount as primary and attach the connectionId.
  const monoLinkedBankIds = new Set(monoConnections.map(c => c.bank_account_id).filter(Boolean));
  const plaidLinkedBankIds = new Set(plaidConnections.map(c => c.bank_account_id).filter(Boolean));
  const stitchLinkedBankIds = new Set(stitchConnections.map(c => c.bank_account_id).filter(Boolean));

  const allAccounts = [
    // All bank accounts - connected ones get their connectionId attached
    ...bankAccounts.map(acc => {
      // iter1225 - Stripe Clearing accounts are system-managed GLs with
      // open_banking_connected=true but no real bank/OB link. Preserve
      // their source verbatim so the downstream UI can lock edits and
      // hide connect/enrich CTAs.
      if (acc.source === 'stripe') {
        return { ...acc, balance: acc.balance || 0 };
      }
      const monoConn = monoConnections.find(c => c.bank_account_id === acc.id);
      // Plaid connections store linked accounts inside an embedded `accounts`
      // array: { connection.id, accounts: [{ account_id }] }. Match on that.
      const plaidConn = plaidConnections.find(c =>
        c.bank_account_id === acc.id
        || (c.accounts || []).some(a => a.account_id === acc.id)
      );
      const stitchConn = stitchConnections.find(c => c.bank_account_id === acc.id);
      if (monoConn) {
        return { ...acc, source: 'mono', connectionId: monoConn.id, balance: acc.balance || 0, provider: 'mono' };
      }
      if (plaidConn || acc.source === 'plaid') {
        // Trust the backend-set source even if we can't find the connection
        // (e.g. user disconnected the connection but the bank_accounts row
        // still lives - we still want to render the Plaid tile so the user
        // can re-link).
        return {
          ...acc,
          source: 'plaid',
          connectionId: plaidConn?.id,
          balance: acc.balance || 0,
          provider: 'plaid',
        };
      }
      if (stitchConn) {
        return { ...acc, source: 'stitch', connectionId: stitchConn.id, balance: acc.balance || 0, provider: 'stitch' };
      }
      // If bank says it's connected but no matching connection found, still mark connected
      if (acc.open_banking_connected) {
        return { ...acc, source: acc.open_banking_source || 'mono', balance: acc.balance || 0 };
      }
      // iter1225 - preserve backend `source` when it's set to a specific
      // system-managed value (e.g., "stripe" for Stripe Clearing GLs). The
      // fallback "manual" tag should only apply to genuinely manual rows.
      if (acc.source && acc.source !== 'manual') {
        return { ...acc, balance: acc.balance || 0 };
      }
      return { ...acc, source: 'manual', balance: acc.balance || 0 };
    }),
    // Only add connections that are NOT linked to any existing bank account (orphan connections)
    ...monoConnections
      .filter(conn => conn.bank_account_id && !bankAccounts.some(acc => acc.id === conn.bank_account_id))
      .map(conn => ({ ...conn, source: 'mono', connectionId: conn.id, balance: conn.balance || 0, provider: 'mono' })),
    ...plaidConnections
      .filter(conn => conn.bank_account_id && !bankAccounts.some(acc => acc.id === conn.bank_account_id))
      .map(conn => ({ ...conn, source: 'plaid', connectionId: conn.id, balance: conn.balance || 0, provider: 'plaid' })),
    ...stitchConnections
      .filter(conn => conn.bank_account_id && !bankAccounts.some(acc => acc.id === conn.bank_account_id))
      .map(conn => ({ ...conn, source: 'stitch', connectionId: conn.id, balance: conn.balance || 0, provider: 'stitch' }))
  ];

  // iter1108 - Sub-account grouping.
  //
  // A Plaid Item can back multiple sub-accounts under one login (e.g.
  // Revolut US = GBP + USD + EUR wallets). Plaid's own API is Item-level:
  // you can only add/remove specific sub-accounts by opening Plaid Link
  // in update-mode with Account Select. We compute per-connection groups
  // so the UI can (a) show a "1 of N sub-accounts" chip on each row,
  // (b) surface the "Manage sub-accounts" button, and (c) enumerate
  // affected siblings in the disconnect confirmation modal.
  const subAccountGroups = (() => {
    const groups = new Map();
    for (const acc of allAccounts) {
      const key = acc.plaid_connection_id || acc.connectionId;
      if (!key || acc.source !== 'plaid') continue;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(acc);
    }
    // Prune singletons - single-account "groups" don't need special treatment.
    for (const k of Array.from(groups.keys())) {
      if ((groups.get(k) || []).length < 2) groups.delete(k);
    }
    return groups;
  })();
  const getSubAccountSiblings = (account) => {
    const key = account.plaid_connection_id || account.connectionId;
    return (key && subAccountGroups.get(key)) || null;
  };
  // Oct 2026 · nested rendering: linked sub-accounts (statement sections, `parent_bank_account_id`) and Plaid
  // sub-accounts (same connection) sit tucked under their head account, which gets a combined-balance header.
  const { orderedAccounts, nestInfo } = (() => {
    const byId = new Map(allAccounts.map((a) => [a.id, a]));
    const bal = (a) => Number(a.functional_balance != null ? a.functional_balance : (a.balance || 0));
    const childrenOf = new Map();
    for (const a of allAccounts) {
      let headId = null;
      if (a.parent_bank_account_id && byId.has(a.parent_bank_account_id)) headId = a.parent_bank_account_id;
      else {
        const sibs = getSubAccountSiblings(a);
        if (sibs && sibs[0] && sibs[0].id !== a.id) headId = sibs[0].id;
      }
      if (headId) { if (!childrenOf.has(headId)) childrenOf.set(headId, []); childrenOf.get(headId).push(a); }
    }
    const info = new Map();
    const ordered = [];
    const placed = new Set();
    for (const a of allAccounts) {
      if (placed.has(a.id)) continue;
      const kids = childrenOf.get(a.id) || [];
      if (!kids.length && [...childrenOf.values()].some((ks) => ks.some((k) => k.id === a.id))) continue;
      ordered.push(a); placed.add(a.id);
      if (kids.length) {
        info.set(a.id, { isHead: true, count: kids.length + 1, combined: bal(a) + kids.reduce((t, k) => t + bal(k), 0) });
        for (const k of kids) {
          ordered.push(k); placed.add(k.id);
          info.set(k.id, { isChild: true, parentName: a.account_name || a.bank_name });
        }
      }
    }
    for (const a of allAccounts) if (!placed.has(a.id)) ordered.push(a);
    return { orderedAccounts: ordered, nestInfo: info };
  })();
  // Any connection Plaid has just flagged as having newly-openable
  // accounts at the bank (webhook NEW_ACCOUNTS_AVAILABLE). Surface a nudge.
  const connectionsWithNewAccounts = plaidConnections.filter(c => c.new_accounts_available);

  // Calculate totals - banks vs cards (cards are Liabilities, not Assets)
  const bankCashAccounts = allAccounts.filter(a => a.account_category !== 'credit_card');
  const cardLiabilityAccounts = allAccounts.filter(a => a.account_category === 'credit_card');
  // iter1091 - Total Cash is always in the business's functional currency.
  // Prefer the new `functional_balance` field (FX-converted server-side)
  // and fall back to legacy `balance` for rows created before the Phase-A
  // projection landed.
  const totalBalance = bankCashAccounts.reduce(
    (sum, acc) => sum + (acc.functional_balance != null ? acc.functional_balance : (acc.balance || 0)),
    0
  );
  const totalCardLiabilities = cardLiabilityAccounts.reduce(
    (sum, acc) => sum + (acc.functional_balance != null ? acc.functional_balance : (acc.balance || 0)),
    0
  );
  const totalAvailableCredit = cardLiabilityAccounts.reduce(
    (sum, acc) => sum + Math.max((acc.credit_limit || 0) - (acc.balance || 0), 0), 0
  );
  const getCurrency = (country) => {
    const currencies = { 'NG': 'NGN', 'GH': 'GHS', 'KE': 'KES', 'ZA': 'ZAR', 'TZ': 'TZS', 'GB': 'GBP', 'US': 'USD' };
    return currencies[country] || 'EUR';
  };
  const defaultCurrency = getCurrency(business?.country);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#108981]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="my-banks-page">
        {/* Sep 2026 · closed-period notice before connect / update / re-auth pulls transactions */}
        {closedPeriodInfo?.closed_through && <ClosedPeriodNotice info={closedPeriodInfo} className="mb-4" />}
        {/* Auto-detect processing banks - shows progress even if user dismissed the sync dialog */}
        {processingBankId && (
          <div className="mb-4">
            <ClassificationProgressTracker
              bankAccountId={processingBankId}
              bankName={processingBankName}
              onComplete={() => { fetchData(); setProcessingBankId(null); }}
            />
          </div>
        )}
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-foreground">My Banks &amp; Cards</h1>
            <p className="text-muted-foreground mt-1">Connect your bank and cards. Transactions import and categorise themselves.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setShowTransferModal(true)}
              className="gap-2"
              data-testid="my-banks-transfer-btn"
            >
              <ArrowLeftRight className="w-4 h-4" />
              <span className="hidden sm:inline">Transfer</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setEnrichingBank({ __new: true })}
              className="gap-2"
              data-testid="my-banks-add-manual-btn"
            >
              <PlusCircle className="w-4 h-4" />
              Add Manual Bank Account
            </Button>
            {(isMonoCountry || isPlaidCountry || isStitchCountry) && (monoConfig.enabled || plaidConfig.enabled || stitchConfig.enabled) && (
              <BankIntegrationTooltip countryCode={business?.country}>
                {isPlaidCountry ? (
                  // Feb 2026 - US users MUST reach Plaid, even if
                  // `/api/plaid/config` hasn't loaded yet. The button
                  // fetches its own link token on click, so it works
                  // regardless of the cached config state. If the
                  // backend doesn't have Plaid credentials configured
                  // it returns a helpful 400 which surfaces as a toast
                  // via `fetchLinkToken` above - so the button stays
                  // clickable rather than mysteriously greyed out.
                  <PlaidLinkButton
                    onSuccess={handlePlaidSuccess}
                    className="gap-2 bg-[#108981] hover:bg-[#108981]/90"
                  >
                    <Link2 className="w-4 h-4" />
                    {`Connect ${getCountryDisplayName(business?.country)} bank or card`}
                  </PlaidLinkButton>
                ) : (
                  <Button
                    className="gap-2 bg-[#108981] hover:bg-[#108981]/90"
                    onClick={isStitchCountry && stitchConfig.enabled ? handleStitchConnect : handleMonoConnect}
                    data-testid="connect-bank-btn"
                  >
                    <Link2 className="w-4 h-4" />
                    {`Connect ${getCountryDisplayName(business?.country)} Bank`}
                  </Button>
                )}
              </BankIntegrationTooltip>
            )}
            {/* iter 908 - Country gating: UK coming soon, everyone else not yet available */}
            {!hasBankConnection && isComingSoonCountry && (
              <Button
                disabled
                className="gap-2 bg-amber-100 text-amber-800 hover:bg-amber-100 cursor-not-allowed"
                data-testid="bank-connection-coming-soon"
              >
                <Link2 className="w-4 h-4" />
                Bank connection: coming soon
              </Button>
            )}
            {!hasBankConnection && !isComingSoonCountry && (
              <Button
                disabled
                className="gap-2 bg-muted text-muted-foreground hover:bg-muted cursor-not-allowed"
                title="Bank connection is not yet available in your country."
                data-testid="bank-connection-unavailable"
              >
                <Link2 className="w-4 h-4" />
                Bank connection not available in your country
              </Button>
            )}
          </div>
        </div>

        {/* Account Separation Tip */}

        {/* iter1204-plaid-ux: resume banner for interrupted Plaid connects.
            Reads localStorage `plaid_pending_connect`; auto-hides if no
            stale marker. When user clicks Resume, reissues a link_token
            and hands it to <PlaidRowLauncher> below via plaidRowLink. */}
        <PendingPlaidResumeBanner
          onResumed={({ linkToken }) => {
            setPlaidRowLink({ linkToken, mode: 'connect', accountId: null });
          }}
        />

        {/* Bank Feed Sync Progress Banner */}
        <BankFeedSyncBanner bankAccounts={bankAccounts} />

        {/* iter959 post-signoff - Unified "Update bank transactions" banner
            (picker variant): same surface here as on Home + Bank Feed
            Center so users see one consistent CTA everywhere. */}
        <MonoBanksRefreshSection
          bankAccounts={bankAccounts}
          onRefreshComplete={fetchData}
        />

        {/* Plaid backfill progress + reauth banners (provider-neutral copy) */}
        <PlaidBackfillProgress plaidConnections={plaidConnections} onComplete={fetchData} />
        <PlaidReauthBanner plaidConnections={plaidConnections} onReconnected={fetchData} />

        {/* 9 Oct 2026 · Bank connection didn't complete → point to the existing statement-upload path. */}
        {connectFallback?.bank_account_id && (() => {
          const fbAcc = (Array.isArray(bankAccounts) ? bankAccounts : []).find((a) => a.id === connectFallback.bank_account_id);
          const fbConnected = fbAcc && (fbAcc.source === 'mono' || fbAcc.source === 'plaid' || fbAcc.open_banking_connected);
          if (!fbAcc || fbConnected) return null;
          const fbName = fbAcc.bank_name || fbAcc.institution_name || 'your bank';
          return (
            <Alert className="border-amber-200 bg-amber-50" data-testid="connect-fallback-banner">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="flex flex-col sm:flex-row sm:items-center gap-2 text-amber-900 text-sm">
                <span className="flex-1">
                  <strong>{fbName}</strong> connection didn&apos;t finish. You can still keep this account up to date by uploading a bank statement — or try connecting again.
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <Button size="sm" className="h-7 text-xs bg-[#108981] hover:bg-[#0d6e68]" data-testid="connect-fallback-upload-btn"
                    onClick={() => { openUploadStarterForAccount(fbAcc.id); dismissConnectFallback(); }}>
                    <Upload className="w-3.5 h-3.5 mr-1" /> Upload statement
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-amber-900" data-testid="connect-fallback-dismiss-btn" onClick={dismissConnectFallback}>
                    Dismiss
                  </Button>
                </span>
              </AlertDescription>
            </Alert>
          );
        })()}

        {/* Remove & Restore - restored accounts that were connected before removal:
            walk the user straight into reconnecting so the missed period is fetched. */}
        {(Array.isArray(bankAccounts) ? bankAccounts : [])
          .filter((a) => a.needs_reconnect && !a.open_banking_connected)
          .map((a) => {
            const provider = a.needs_reconnect_provider;
            const plaidOk = plaidConfig.enabled && isPlaidCountry;
            const monoOk = monoConfig.enabled && isMonoCountry;
            const useMono = provider === 'mono' ? monoOk : (!plaidOk && monoOk);
            const usePlaid = provider === 'plaid' ? plaidOk : (!useMono && plaidOk);
            return (
              <RestoredReconnectBanner
                key={`restored-${a.id}`}
                account={a}
                canReconnect={usePlaid || useMono}
                reconnecting={plaidRowLoading === a.id}
                onReconnect={(acct) => {
                  if (usePlaid) return startPlaidRowConnect(acct);
                  setConsentProvider('mono');
                  setPendingConnection('mono');
                  window.__pendingBankAccountId = acct.id;
                  setShowConsentDialog(true);
                }}
                onUpload={(acct) => setStatementUploadFor(acct)}
                onChange={fetchData}
              />
            );
          })}

        {/* Summary Cards - compact KPI strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
          <Card className="bg-gradient-to-br from-[#283F8B] to-[#283F8B]/80 text-white border-0" data-testid="kpi-total-cash">
            <CardContent className="p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-white/70 text-xs">Total Cash</p>
                <Wallet className="w-4 h-4 text-white/40 shrink-0" />
              </div>
              <p className="text-lg lg:text-xl font-bold leading-tight truncate" title={formatCurrency(totalBalance, defaultCurrency)} data-testid="kpi-total-cash-value">
                {formatCompactCurrency(totalBalance, defaultCurrency)}
              </p>
              <p className="text-white/60 text-[10px] mt-0.5">{bankCashAccounts.length} bank account(s)</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-muted-foreground text-xs">Connected Banks</p>
                <Globe className="w-4 h-4 text-blue-500/40 shrink-0" />
              </div>
              <p className="text-lg lg:text-xl font-bold leading-tight" data-testid="kpi-connected-banks-value">{allAccounts.filter(acc => acc.source && acc.source !== 'manual').length}</p>
              <p className="text-muted-foreground text-[10px] mt-0.5">Auto-syncing transactions</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-muted-foreground text-xs">Manual Accounts</p>
                <Building2 className="w-4 h-4 text-[#108981]/40 shrink-0" />
              </div>
              <p className="text-lg lg:text-xl font-bold leading-tight" data-testid="kpi-manual-accounts-value">{allAccounts.filter(acc => acc.source === 'manual').length}</p>
              <p className="text-muted-foreground text-[10px] mt-0.5">Statement upload required</p>
            </CardContent>
          </Card>

          <Card
            className={cardLiabilityAccounts.length === 0 ? '' : 'bg-gradient-to-br from-rose-600 to-rose-700 text-white border-0'}
            data-testid="kpi-card-liabilities"
          >
            <CardContent className="p-3">
              <div className="flex items-center justify-between gap-2">
                <p className={cardLiabilityAccounts.length === 0 ? 'text-muted-foreground text-xs' : 'text-white/70 text-xs'}>Card Liabilities</p>
                <CreditCard className={`w-4 h-4 shrink-0 ${cardLiabilityAccounts.length === 0 ? 'text-rose-600/40' : 'text-white/40'}`} />
              </div>
              <p className="text-lg lg:text-xl font-bold leading-tight truncate" title={formatCurrency(totalCardLiabilities, defaultCurrency)}>
                {formatCompactCurrency(totalCardLiabilities, defaultCurrency)}
              </p>
              <p className={`text-[10px] mt-0.5 truncate ${cardLiabilityAccounts.length === 0 ? 'text-muted-foreground' : 'text-white/70'}`}>
                {cardLiabilityAccounts.length === 0
                  ? 'No credit cards connected'
                  : `${cardLiabilityAccounts.length} card(s) • ${formatCompactCurrency(totalAvailableCredit, defaultCurrency)} available`}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Bank Accounts List */}
        <Card>
          <CardHeader className="pb-2 px-3 sm:px-5 pt-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#283F8B]" />
              All Accounts &amp; Cards
            </CardTitle>
            <CardDescription className="text-xs">
              Tap an account to see transactions or use the menu
            </CardDescription>
          </CardHeader>
          <CardContent className="px-3 sm:px-5">
            {allAccounts.length === 0 ? (
              <div className="text-center py-12">
                <Wallet className="w-16 h-16 text-muted-foreground/20 mx-auto mb-4" />
                <h3 className="font-semibold text-foreground mb-2">No accounts yet</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                  Connect a bank or card and your transactions import, categorise and reconcile themselves.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setEnrichingBank({ __new: true })}
                    data-testid="my-banks-empty-add-manual-btn"
                  >
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Add Manual Bank Account
                  </Button>
                  {/* Bank Integration with Supported Banks Tooltip */}
                  {(isMonoCountry || isPlaidCountry || isStitchCountry) && (monoConfig.enabled || plaidConfig.enabled || stitchConfig.enabled) && (
                    <BankIntegrationTooltip countryCode={business?.country}>
                      {isPlaidCountry ? (
                        <PlaidLinkButton
                          onSuccess={handlePlaidSuccess}
                          className="bg-[#108981] hover:bg-[#108981]/90"
                        >
                          <Link2 className="w-4 h-4 mr-2" />
                          {`Connect ${getCountryDisplayName(business?.country)} bank or card`}
                        </PlaidLinkButton>
                      ) : (
                        <Button
                          className="bg-[#108981] hover:bg-[#108981]/90"
                          onClick={isStitchCountry && stitchConfig.enabled ? handleStitchConnect : handleMonoConnect}
                          data-testid="connect-bank-empty-btn"
                        >
                          <Link2 className="w-4 h-4 mr-2" />
                          {`Connect ${getCountryDisplayName(business?.country)} Bank`}
                        </Button>
                      )}
                    </BankIntegrationTooltip>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* iter1108 - Plaid NEW_ACCOUNTS_AVAILABLE webhook nudge.
                    When Plaid tells us the user has newly-shareable accounts
                    at the bank (e.g. a wallet they opened after initial link),
                    we surface a one-click "Review" that opens update-mode
                    Account Select for that specific connection. */}
                {connectionsWithNewAccounts && connectionsWithNewAccounts.length > 0 && (
                  <div
                    className="p-4 border border-sky-300 bg-sky-50 rounded-lg flex items-start gap-3"
                    data-testid="plaid-new-accounts-nudge"
                  >
                    <Wallet className="w-5 h-5 text-sky-700 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 text-sm text-sky-900">
                      <p className="font-semibold mb-0.5">
                        {connectionsWithNewAccounts.length === 1
                          ? `New accounts available at ${connectionsWithNewAccounts[0].institution_name || 'your bank'}`
                          : `New accounts available at ${connectionsWithNewAccounts.length} connected banks`}
                      </p>
                      <p className="text-sky-800/90">
                        Your bank has more accounts you can share. Choose which ones sync.
                      </p>
                    </div>
                    {connectionsWithNewAccounts.length === 1 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-sky-400 text-sky-800"
                        onClick={() => {
                          const conn = connectionsWithNewAccounts[0];
                          const stub = allAccounts.find(a => (a.plaid_connection_id === conn.id));
                          if (stub) openManageSubAccounts(stub);
                        }}
                        data-testid="plaid-new-accounts-review-btn"
                        disabled={managingAccounts}
                      >
                        Review
                      </Button>
                    )}
                  </div>
                )}
                {orderedAccounts.map((account, accountIdx) => {
                  const nest = nestInfo.get(account.id) || {};
                  const isConnected = account.source === 'mono' || account.source === 'plaid' || account.source === 'stitch' || account.open_banking_connected;
                  const isCard = account.account_category === 'credit_card';
                  const accountId = account.id;
                  const displayName = account.institution_name || account.bank_name || account.account_name || 'Bank Account';
                  const accountNumber = account.account_number || account.mask || '****';
                  // iter1091 - prefer the native currency exposed by the
                  // backend Phase-A projection; fall back to legacy
                  // `account.currency` only when the new field is missing
                  // (rows from very old sync jobs that were not touched
                  // by the backfill script).
                  const currency = account.native_currency || account.currency || defaultCurrency;
                  const availableCredit = isCard ? Math.max((account.credit_limit || 0) - (account.balance || 0), 0) : null;
                  // iter1091 - dual balance projection. Native comes from
                  // the last provider / statement snapshot; functional
                  // comes from the GL. Two independent numbers.
                  const nativeBalance = account.native_balance != null
                    ? account.native_balance
                    : (account.balance || 0);
                  const functionalBalance = account.functional_balance != null
                    ? account.functional_balance
                    : (account.balance || 0);
                  const functionalCurrency = account.functional_currency || defaultCurrency;
                  const reconStatus = account.reconciliation_status || 'not_reconciled';
                  const lastVariance = account.last_variance;
                  const lastVarianceCurrency = account.last_variance_currency || currency;
                  const lastReconciledAt = account.last_reconciled_at;
                  // iter1108 - siblings under the same Plaid Item.
                  const siblings = getSubAccountSiblings(account);
                  const siblingIndex = siblings
                    ? (siblings.findIndex(s => s.id === accountId) + 1) || 1
                    : null;
                  
                  return (
                    <React.Fragment key={accountId}>
                    {nest.isHead && (
                      <div
                        className="flex items-center justify-between gap-2 px-1 pt-1 text-xs text-slate-600"
                        data-testid={`account-group-header-${accountId}`}
                      >
                        <span className="inline-flex items-center gap-1.5 font-medium">
                          <Wallet className="w-3.5 h-3.5 text-emerald-600" />
                          {displayName} · {nest.count} linked accounts
                        </span>
                        <span className="font-semibold text-slate-800" data-testid={`account-group-combined-${accountId}`}>
                          Combined {formatCurrency(nest.combined, currency)}
                        </span>
                      </div>
                    )}
                    <div
                      data-testid={`bank-account-${accountId}`}
                      className={`rounded-2xl border ${
                        isCard
                          ? 'border-rose-200 bg-rose-50/30'
                          : 'border-slate-200 bg-white'
                      } p-2 sm:p-2.5 shadow-sm hover:shadow-md transition-shadow ${nest.isChild ? 'ml-5 sm:ml-8 border-l-4 border-l-emerald-300' : ''}`}
                    >
                      {nest.isChild && (
                        <p className="text-[11px] text-emerald-700 px-1 pb-1 inline-flex items-center gap-1" data-testid={`nested-under-${accountId}`}>
                          <CornerDownRight className="w-3 h-3" /> Linked to {nest.parentName}
                        </p>
                      )}
                      {/* iter1094-bank-card-separation - wrap each
                          account in a distinct bordered card. Previously
                          the parent PlaidAccountTile header AND the
                          child account row (both saying e.g.
                          "Revolut (US) ****2499") were rendered as
                          siblings inside `space-y-3` with no visual
                          boundary, which made them look like duplicate
                          entries. The bordered wrapper now makes it
                          obvious they belong to the same account, and
                          keeps a clear break between neighbouring
                          accounts. */}
                      <div className="flex items-center justify-between gap-2 mb-1.5 px-1">
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 truncate">
                          Account {accountIdx + 1}
                          <span className="mx-1 text-slate-300">·</span>
                          {displayName}
                          {account.parent_bank_account_id && (
                            <span
                              className="ml-2 normal-case tracking-normal inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 px-1.5 py-px text-[10px] font-semibold text-emerald-700"
                              data-testid={`sub-account-tag-${accountId}`}
                              title="Linked sub-account: imported from the same statement as its parent account; sweeps between them are recognised as transfers"
                            >
                              Sub-account · {account.sub_account_title || account.sub_account_kind || 'linked'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {!isConnected && (
                            <button
                              data-testid={`card-upload-statement-btn-${accountId}`}
                              onClick={() => openUploadStarterForAccount(accountId)}
                              className="inline-flex items-center gap-1 text-[11px] text-white bg-[#108981] hover:bg-[#0b6b65] rounded-md px-2 py-0.5"
                              title="Upload a bank statement for this account"
                            >
                              <Upload className="w-3 h-3" /> Upload
                            </button>
                          )}
                          <button
                            data-testid={`statement-vault-btn-${accountId}`}
                            onClick={() => setStatementVaultFor(account)}
                            className="text-[11px] text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 rounded-md px-2 py-0.5"
                          >
                            Statements
                          </button>
                        </div>
                      </div>
                      {/* Classification Progress for this account */}
                      <ClassificationProgressTracker
                        bankAccountId={accountId}
                        bankName={displayName}
                        onComplete={fetchData}
                        compact
                      />
                      {/* iter959 post-signoff - NigeriaBankTile per-account
                          "Reconnect" attention panel removed. The unified
                          "Update bank transactions" banner at the top of the
                          page now covers this need, and the per-bank 3-dot
                          menu ("Update bank transactions" item) provides
                          per-account access. This eliminates the duplicate
                          "Reconnect" surface that was noisy for non-accountants. */}
                      {(account.source === 'mono' || account.open_banking_source === 'mono') && (
                        <div className="mb-2">
                          {/* iter959 Phase 3 - retry CTA when the previous backlog charge failed */}
                          <RetryBacklogButton
                            bankAccountId={accountId}
                            bankName={displayName}
                          />
                          {/* iter1207 - re-open the backlog confirmation modal (Mono) */}
                          <RetryBacklogConfirmButton
                            bankAccountId={accountId}
                            connectionId={monoConnections.find(c =>
                              c.mono_account_id === account.mono_account_id
                              || c.bank_account_id === account.id
                            )?.id}
                            provider="mono"
                            bankName={displayName}
                          />
                          {/* iter959 Phase 3 (H+I) - backlog-gap resolution */}
                          <BankGapBanner
                            bankAccountId={accountId}
                            onUploadClick={() => openUploadStarterForAccount(accountId)}
                            onResolved={fetchData}
                          />
                          {/* iter1268 - provable mid-history gap prompt */}
                          <HistoryGapBanner
                            bankAccountId={accountId}
                            onUploadClick={() => openUploadStarterForAccount(accountId)}
                          />
                        </div>
                      )}
                      {/* UK/EU/US/CA Plaid panel - mirror of NigeriaBankTile so
                          the experience feels consistent across regions. Shown
                          for Plaid-connected accounts (banks AND credit cards). */}
                      {(account.source === 'plaid' || account.open_banking_source === 'plaid') && (
                        <div className="mb-2">
                          <PlaidAccountTile
                            bankAccount={account}
                            connection={plaidConnections.find(c =>
                              c.bank_account_id === account.id
                              || (c.accounts || []).some(a => a.account_id === account.id)
                            ) || null}
                            onRefreshed={fetchData}
                          />
                          {/* iter959 Phase 3 - retry CTA + backlog-gap resolution for Plaid parity */}
                          <RetryBacklogButton
                            bankAccountId={accountId}
                            bankName={displayName}
                            provider="plaid"
                          />
                          {/* iter1207 - re-open the backlog confirmation modal (Plaid) */}
                          <RetryBacklogConfirmButton
                            bankAccountId={accountId}
                            connectionId={plaidConnections.find(c =>
                              c.bank_account_id === account.id
                              || (c.accounts || []).some(a => a.account_id === account.id)
                            )?.id}
                            provider="plaid"
                            bankName={displayName}
                          />
                          <BankGapBanner
                            bankAccountId={accountId}
                            onUploadClick={() => openUploadStarterForAccount(accountId)}
                            onResolved={fetchData}
                          />
                          {/* iter1268 - provable mid-history gap prompt */}
                          <HistoryGapBanner
                            bankAccountId={accountId}
                            onUploadClick={() => openUploadStarterForAccount(accountId)}
                          />
                        </div>
                      )}
                      {/* Iter 804 - Statement Vault buttons (provider-neutral) */}
                      <div className="flex items-center gap-2 ml-1 flex-wrap empty:hidden [&:has(>*)]:mb-1.5">
                        {/* iter1213 - Pending Plaid OB pill (opens the mandatory
                            confirmation dialog with education). No-op if this
                            account has no pending OB task. */}
                        <PendingOBAccountPill
                          bankAccountId={accountId}
                          onSaved={fetchData}
                        />
                        {(account.ob_needed_effective ?? account.ob_needed) && !isConnected && (
                          <button
                            data-testid={`set-opening-balance-btn-${accountId}`}
                            onClick={() => setObDialogAccount(account)}
                            className="text-xs text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-300 rounded px-2 py-1 flex items-center gap-1.5 font-medium"
                          >
                            Opening balance needed: set it now
                          </button>
                        )}
                        {/* 9 Oct 2026 · Bank-sync action on the card itself (was hidden behind the 3-dot menu).
                            Manual → "Connect bank sync"; Mono connected → "Update bank transactions";
                            Plaid connected → only "Reconnect" when the backend flags needs_reconnect. */}
                        {!isConnected && !isCard && monoConfig.enabled && isMonoCountry && (
                          <button
                            data-testid={`card-connect-mono-${accountId}`}
                            onClick={() => {
                              setConsentProvider('mono');
                              setPendingConnection('mono');
                              window.__pendingBankAccountId = accountId;
                              setShowConsentDialog(true);
                            }}
                            disabled={connecting}
                            className="text-xs text-white bg-[#108981] hover:bg-[#0d6e68] rounded px-2 py-1 flex items-center gap-1.5 font-medium disabled:opacity-60"
                          >
                            <Link2 className="w-3.5 h-3.5" />
                            {account.disconnected_from_mono_at ? 'Reconnect bank sync' : 'Connect bank sync'}
                          </button>
                        )}
                        {!isConnected && !isCard && plaidConfig.enabled && isPlaidCountry && (
                          <button
                            data-testid={`card-connect-plaid-${accountId}`}
                            onClick={() => startPlaidRowConnect(account)}
                            disabled={plaidRowLoading === accountId}
                            className="text-xs text-white bg-[#108981] hover:bg-[#0d6e68] rounded px-2 py-1 flex items-center gap-1.5 font-medium disabled:opacity-60"
                          >
                            {plaidRowLoading === accountId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                            {account.disconnected_from_plaid_at ? 'Reconnect bank sync' : 'Connect bank sync'}
                          </button>
                        )}
                        {isConnected && (account.source === 'mono' || account.open_banking_source === 'mono') && (
                          <button
                            data-testid={`card-update-mono-${accountId}`}
                            onClick={() => handleMonoUpdate(accountId)}
                            disabled={connecting}
                            className="text-xs text-[#108981] bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded px-2 py-1 flex items-center gap-1.5 font-medium disabled:opacity-60"
                          >
                            <RefreshCw className="w-3.5 h-3.5" /> Update bank transactions
                          </button>
                        )}
                        {isConnected && account.source === 'plaid' && account.attention_reason === 'needs_reconnect' && (
                          <button
                            data-testid={`card-reconnect-plaid-${accountId}`}
                            onClick={() => startPlaidRowReauth(account)}
                            disabled={plaidRowLoading === accountId}
                            className="text-xs text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-300 rounded px-2 py-1 flex items-center gap-1.5 font-medium disabled:opacity-60"
                          >
                            {plaidRowLoading === accountId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Reconnect bank sync
                          </button>
                        )}
                        <ConnectUnconfirmedNote accountId={accountId} entry={connectUnconfirmed[accountId]} onDismiss={dismissConnectUnconfirmed} />
                        {/* iter1225 - Enrich international transfer details.
                            Show when a Plaid/Mono account is missing BOTH
                            SWIFT and IBAN - the two fields customers need
                            to make a foreign-currency wire. Plaid never
                            returns the full account number so we don't
                            gate the CTA on that. Once wire details are
                            filled, the CTA hides. Never shown on Stripe
                            clearing accounts (auto-managed). */}
                        {account.source !== 'stripe' &&
                          (account.source === 'plaid' || account.source === 'mono' || account.open_banking_source === 'mono' || account.open_banking_source === 'plaid') &&
                          !account.bic_swift && !account.iban && (
                          <button
                            data-testid={`enrich-transfer-details-btn-${accountId}`}
                            onClick={() => setEnrichingBank(account)}
                            className="text-xs text-[#283F8B] bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded px-2 py-1 flex items-center gap-1.5 font-medium"
                            title="Add your full account number, SWIFT/BIC and IBAN so customers can pay you."
                          >
                            Add international transfer details
                          </button>
                        )}
                        {/* iter1237 - Removed the always-visible "Upload
                            statement" pill (opened the date-range
                            StatementUploadDialog / statement-vault sync flow).
                            The 3-dot menu's "Upload Statement" (async backlog
                            flow via useStatementUploader) is the single entry
                            point users rely on. StatementUploadDialog is still
                            reachable via BankGapBanner (data-gap resolution)
                            and NigeriaBankTile (reauth backfill). */}
                      </div>
                      <div 
                        className={`flex flex-wrap sm:flex-nowrap items-center justify-between p-2.5 sm:p-3 hover:bg-muted/50 rounded-xl transition-colors group gap-x-2 gap-y-1.5 ${expandedAccount === accountId ? 'rounded-b-none border-b border-border' : ''} ${isCard ? 'bg-rose-50/60 border-l-4 border-rose-500' : 'bg-muted/30'}`}
                        data-testid={`account-tile-${isCard ? 'card' : 'bank'}-${accountId}`}
                      >
                      {/* Account Info */}
                      <div className="flex items-center gap-2.5 sm:gap-3 flex-1 min-w-0">
                        <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 ${
                          isCard ? 'bg-rose-100' : (isConnected ? 'bg-green-100' : 'bg-gray-100')
                        }`}>
                          {isCard ? (
                            <CreditCard className="w-5 h-5 text-rose-600" />
                          ) : isConnected ? (
                            <span className="text-xl">{getCountryFlag(business?.country)}</span>
                          ) : (
                            <Building2 className="w-5 h-5 text-gray-600" />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-semibold text-sm text-foreground truncate">{displayName}</p>
                            {isCard && (
                              <Badge variant="outline" className="text-rose-700 border-rose-300 bg-rose-50 text-xs">
                                <CreditCard className="w-3 h-3 mr-1" />
                                Credit card
                              </Badge>
                            )}
                            {isConnected && !isCard && (
                              <Badge variant="outline" className="text-green-600 border-green-300 text-[10px] px-1.5 py-0">
                                <CheckCircle2 className="w-3 h-3 mr-0.5" />
                                Connected
                              </Badge>
                            )}
                            {isConnected && isCard && (
                              <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Synced
                              </Badge>
                            )}
                            {account.is_default && (
                              <Badge className="bg-[#283F8B] text-xs">Default</Badge>
                            )}
                            {/* iter1108 - "N of M sub-accounts" chip.
                                Signals to the user that this row is one of
                                several under the same bank login. Clicking
                                Disconnect will show the sibling list; users
                                who want to add/remove a single wallet get
                                the "Manage sub-accounts" option in the row
                                menu. */}
                            {siblings && (
                              <Badge
                                variant="outline"
                                className="text-sky-700 border-sky-300 bg-sky-50 text-xs gap-1"
                                data-testid={`subaccount-chip-${accountId}`}
                                title={`One of ${siblings.length} sub-accounts sharing the ${displayName} connection`}
                              >
                                <Wallet className="w-3 h-3" />
                                Sub-account {siblingIndex} of {siblings.length}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {(() => {
                              const friendlyType = ((s) => {
                                const sub = (s || '').toLowerCase();
                                if (sub === 'checking') return business?.country === 'GB' ? 'Current account' : 'Checking';
                                if (sub === 'savings') return 'Savings';
                                if (sub === 'credit card') return 'Credit card';
                                if (sub === 'money market') return 'Money market';
                                return null;
                              })(account.subtype);
                              const friendly = friendlyType || (isCard ? 'Credit card' : (
                                ['plaid', 'mono', 'stitch'].includes(account.source) ? 'Bank account' : 'Manual'
                              ));
                              return `${friendly} • •••• ${accountNumber.slice(-4)}`;
                            })()}
                            {isCard && account.credit_limit ? ` • ${formatCurrency(availableCredit, currency)} available` : null}
                          </p>
                        </div>
                      </div>

                      {/* Balance - iter1094 dual display:
                          primary = native GL balance (in bank currency,
                          sum of native_debit − native_credit on the
                          bank GL account); sub-line = functional GL
                          balance (in reporting currency, sum of
                          debit − credit). Both are pulled from
                          journals_unified, satisfying the accounting
                          standard that a foreign account should show
                          its native GL first. For same-currency
                          accounts the sub-line hides automatically. */}
                      <div className="text-right shrink-0 w-full sm:w-auto order-3 sm:order-2 pl-11 sm:pl-0">
                        <p className={`text-sm sm:text-base font-bold whitespace-nowrap ${isCard ? 'text-rose-700' : 'text-foreground'}`}
                           data-testid={`account-balance-native-${accountId}`}>
                          {formatCurrency(nativeBalance, currency)}
                        </p>
                        {currency !== functionalCurrency && (
                          <p className="text-[11px] text-muted-foreground" data-testid={`account-balance-functional-${accountId}`}>
                            ≈ {formatCurrency(functionalBalance, functionalCurrency)} {functionalCurrency}
                          </p>
                        )}
                        {/* iter1094-bank-balance-label - the balance shown
                            IS the GL (ledger) balance computed from
                            journals_unified native lines, not the raw
                            provider snapshot. Previous label "(per bank)"
                            misled users into thinking it was the bank
                            statement side - it wasn't. Now labelled
                            explicitly as "per ledger" so it aligns with
                            the "Balance per ledger" figure on the Bank
                            Reconciliation page. */}
                        <p className="text-[10px] text-muted-foreground leading-tight" title="Ledger balance (from posted journals). Matches the 'Balance per ledger' shown on the Bank Reconciliation page.">
                          {isCard ? 'owed' : `${currency} · per ledger`}
                        </p>
                        {/* iter1091 - reconciliation status pill. Kept
                            compact under the balance so it visually
                            belongs to this bank account only. */}
                        {!isCard && (
                          <div className="mt-0.5 flex items-center justify-end gap-1" data-testid={`account-recon-status-${accountId}`}>
                            {reconStatus === 'reconciled' ? (
                              <>
                                <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                <span className="text-[11px] text-emerald-700 font-medium">
                                  Reconciled
                                  {lastReconciledAt ? (
                                    <span className="text-muted-foreground font-normal"> · {String(lastReconciledAt).slice(0, 10)}</span>
                                  ) : null}
                                </span>
                              </>
                            ) : reconStatus === 'items_to_reconcile' ? (
                              <>
                                <AlertCircle className="w-3 h-3 text-amber-600" />
                                <button
                                  onClick={() => navigate(`/reconciliation?bank_id=${accountId}`)}
                                  className="text-[11px] text-amber-700 font-medium hover:underline"
                                  data-testid={`account-reconcile-cta-${accountId}`}
                                >
                                  Items to reconcile →
                                </button>
                              </>
                            ) : reconStatus === 'difference_exists' ? (
                              <>
                                <AlertCircle className="w-3 h-3 text-red-600" />
                                <button
                                  onClick={async () => {
                                    // iter1234-hotfix - Phantom variance
                                    // self-heal. If the GL is empty but a
                                    // provider snapshot lingers (usually a
                                    // pre-cascade-fix statement delete),
                                    // clear the orphan bank_daily_balances
                                    // rows first so we don't punt the user
                                    // to the Reconciliation page for a
                                    // variance that has no real cause.
                                    const glZero = Math.abs(nativeBalance || 0) < 0.01
                                                   && Math.abs(functionalBalance || 0) < 0.01;
                                    if (glZero) {
                                      try {
                                        const res = await axios.post(
                                          `${API}/bank-feeds/bank-accounts/${accountId}/clear-phantom-snapshots`
                                        );
                                        if (res.data?.orphans_removed > 0) {
                                          toast.success(
                                            `Cleared ${res.data.orphans_removed} phantom balance snapshot(s). The variance should disappear on refresh.`
                                          );
                                          await fetchData();
                                          return;
                                        }
                                      } catch (e) {
                                        // Fall through to the normal
                                        // reconciliation flow on any
                                        // unexpected error - never block
                                        // the user from getting to the
                                        // reconciliation page.
                                      }
                                    }
                                    navigate(`/bank-feeds?tab=reconciliation&bank_account_id=${accountId}`);
                                  }}
                                  className="text-[11px] text-red-700 font-medium hover:underline"
                                  data-testid={`account-reconcile-cta-${accountId}`}
                                >
                                  {lastVariance != null
                                    ? `Variance ${formatCurrency(lastVariance, lastVarianceCurrency)} · Reconcile now →`
                                    : 'Reconcile now →'}
                                </button>
                              </>
                            ) : (
                              <>
                                <AlertCircle className="w-3 h-3 text-slate-500" />
                                <button
                                  onClick={() => navigate(`/reconciliation?bank_id=${accountId}`)}
                                  className="text-[11px] text-slate-600 font-medium hover:underline"
                                  data-testid={`account-reconcile-cta-${accountId}`}
                                >
                                  Start reconciliation →
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 order-2 sm:order-3 shrink-0">
                        {/* iter1384 · Fill Gap chip — persists until the
                            gap window is closed by a later manual
                            upload or OB replay, then auto-clears. */}
                        {gapReminders.filter((r) => r.bank_account_id === accountId).map((rem) => (
                          <button
                            key={rem.id}
                            type="button"
                            onClick={() => setOpenGapFillFor(rem)}
                            title={rem.reason === 'start_date_moved'
                              ? `Your start date moved back — upload a statement covering ${rem.gap_start} → ${rem.gap_end}.`
                              : rem.reason === 'provider_history_limit'
                                ? `Your bank connection could not pull history back to ${rem.gap_start} — upload a statement covering ${rem.gap_start} → ${rem.gap_end}, or tell us the account was opened later.`
                                : `Statement "${rem.deleted_filename || 'deleted'}" left a gap ${rem.gap_start} → ${rem.gap_end}. Click to fill.`}
                            className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-100 transition-colors"
                            data-testid={`fill-gap-chip-${accountId}`}
                          >
                            <AlertCircle className="w-3 h-3" />
                            Fill gap
                          </button>
                        ))}
                        {/* Quick Action Buttons */}
                        <div className="hidden sm:flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {/* Upload Statement */}
                          <input
                            type="file"
                            id={`upload-${accountId}`}
                            accept=".csv,.txt,.pdf,.xls,.xlsx,.ofx,.qfx"
                            multiple
                            onChange={(e) => handleStatementUpload(e, accountId)}
                            className="hidden"
                            data-testid={`account-upload-input-${accountId}`}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openUploadStarterForAccount(accountId)}
                            title="Upload Statement"
                            className="h-8 px-2"
                            data-testid={`account-upload-statement-btn-${accountId}`}
                          >
                            <Upload className="w-4 h-4" />
                          </Button>
                          
                          {/* Get Paid */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/invoices/new?bank=${accountId}`)}
                            title="Get Paid (Create Invoice)"
                            className="h-8 px-2 text-green-600 hover:text-green-700"
                          >
                            <ArrowDownLeft className="w-4 h-4" />
                          </Button>
                          
                          {/* Pay Bills */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/bills/new?bank=${accountId}`)}
                            title="Pay Bills"
                            className="h-8 px-2 text-red-600 hover:text-red-700"
                          >
                            <ArrowUpRight className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* iter1230 - Retry backlog fee pill (per bank account).
                            iter1238 Phase 8.9 - now a dropdown offering
                            BOTH "Confirm backlog fee" AND "Cancel this
                            upload" so the user controls the flow instead
                            of a page-load modal ambush. */}
                        {pendingJobsByAccount[accountId] && (() => {
                          // iter1238 Phase 8.10 · P1.5 — render different pill
                          // states depending on the job's phase:
                          //   • needs_range_retry → blue "Retrying page range X · attempt Y/10"
                          //   • pending_payment   → amber "Backlog fee declined"
                          //   • awaiting_confirmation → amber "Backlog fee pending"
                          const j = pendingJobsByAccount[accountId];
                          const isRetrying = j.status === 'needs_range_retry';
                          // iOS App Store build: fee pills must never render in-app
                          // (web-initiated paid jobs are confirmed on the web).
                          if (isIOSNative() && !isRetrying) return null;
                          const isDeclined = j.status === 'pending_payment';
                          const rs = j.retry_state || null;
                          const rangeId = rs?.top_retry?.range_id;
                          const attempt = rs?.top_retry?.attempt ?? (rs?.incomplete_ranges?.length ? 0 : null);
                          const maxAttempts = rs?.max_attempts ?? 10;
                          const pillClass = isRetrying
                            ? 'h-8 px-2 border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100'
                            : 'h-8 px-2 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100';
                          const label = isRetrying
                            ? (rangeId
                                ? `Retrying pages ${rangeId.replace(/^r/, '')} · attempt ${(attempt || 0) + 1}/${maxAttempts}`
                                : `Retrying · ${rs?.incomplete_ranges?.length || '?'} range(s) queued`)
                            : (isDeclined ? 'Backlog fee declined' : 'Backlog fee pending');
                          const IconEl = isRetrying ? Clock : CreditCard;
                          const title = isRetrying
                            ? `${rs?.incomplete_ranges?.length || 0} page range(s) will retry automatically (up to ${maxAttempts} attempts). You can cancel now if you\u2019d rather re-upload.`
                            : (isDeclined
                                ? `Payment declined - ${j.last_decline_reason || 'retry or cancel'}`
                                : 'Backlog fee awaiting your decision');
                          return (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className={pillClass}
                                data-testid={`pending-backlog-menu-${accountId}`}
                                title={title}
                              >
                                <IconEl className="w-3.5 h-3.5 sm:mr-1" />
                                <span className="text-xs hidden sm:inline">{label}</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-64">
                              {!isRetrying && (
                                <DropdownMenuItem
                                  onClick={() => handleRetryBacklogFee(accountId)}
                                  data-testid={`pending-backlog-confirm-${accountId}`}
                                >
                                  <CreditCard className="w-4 h-4 mr-2 text-emerald-600" />
                                  <span>{isDeclined ? 'Retry backlog fee' : 'Review & confirm'}</span>
                                </DropdownMenuItem>
                              )}
                              {isRetrying && (
                                <div className="px-2 py-1.5 text-xs text-slate-600 space-y-1" data-testid={`pending-backlog-retry-detail-${accountId}`}>
                                  <div className="font-medium text-slate-700">Parse retry in progress</div>
                                  {rs?.top_retry?.reason && (
                                    <div className="text-slate-500">Reason: {String(rs.top_retry.reason).replace(/_/g, ' ')}</div>
                                  )}
                                  {rs?.top_retry?.retry_at && (
                                    <div className="text-slate-500">Next attempt: {new Date(rs.top_retry.retry_at).toLocaleTimeString()}</div>
                                  )}
                                  <div className="text-slate-500">
                                    Retries continue automatically up to {maxAttempts} times over 12 h.
                                  </div>
                                </div>
                              )}
                              {!isRetrying && <DropdownMenuSeparator />}
                              <DropdownMenuItem
                                onClick={() => askCancelBacklog(accountId)}
                                className="text-red-600 focus:text-red-700"
                                data-testid={`pending-backlog-cancel-${accountId}`}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Cancel this upload
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          );
                        })()}

                        {/* More Actions Menu */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`account-actions-menu-${accountId}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => toggleLedger(accountId)}>
                              <Eye className="w-4 h-4 mr-2" />
                              {expandedAccount === accountId ? 'Hide Transactions' : 'View Transactions'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openUploadStarterForAccount(accountId)} data-testid={`account-upload-statement-menu-${accountId}`}>
                              <Upload className="w-4 h-4 mr-2" />
                              Upload Statement
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => navigate(`/invoices/new?bank=${accountId}`)} className="text-green-600">
                              <ArrowDownLeft className="w-4 h-4 mr-2" />
                              Get Paid (Invoice)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/bills/new?bank=${accountId}`)} className="text-red-600">
                              <ArrowUpRight className="w-4 h-4 mr-2" />
                              Pay Bills
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {!isConnected && (
                              <>
                                {monoConfig.enabled && isMonoCountry && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setConsentProvider('mono');
                                      setPendingConnection('mono');
                                      // Store the bank account id for linking after consent
                                      window.__pendingBankAccountId = accountId;
                                      setShowConsentDialog(true);
                                    }}
                                    data-testid={`connect-mono-${accountId}`}
                                  >
                                    <Link2 className="w-4 h-4 mr-2 text-[#108981]" />
                                    <span className="text-[#108981] font-medium">
                                      {account.disconnected_from_mono_at ? 'Reconnect bank sync' : 'Connect bank sync'}
                                    </span>
                                  </DropdownMenuItem>
                                )}
                                {/* iter1112 - Plaid Connect / Reconnect from
                                    the row. Fires an ad-hoc Plaid Link
                                    session; backend orphan matcher merges
                                    this manual row by (institution, mask)
                                    so opening balance + history stay put. */}
                                {plaidConfig.enabled && isPlaidCountry && (
                                  <DropdownMenuItem
                                    onClick={() => startPlaidRowConnect(account)}
                                    disabled={plaidRowLoading === accountId}
                                    data-testid={`connect-plaid-${accountId}`}
                                  >
                                    {plaidRowLoading === accountId ? (
                                      <Loader2 className="w-4 h-4 mr-2 text-[#108981] animate-spin" />
                                    ) : (
                                      <Link2 className="w-4 h-4 mr-2 text-[#108981]" />
                                    )}
                                    <span className="text-[#108981] font-medium">
                                      {account.disconnected_from_plaid_at ? 'Reconnect bank sync' : 'Connect bank sync'}
                                    </span>
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => navigate(`/settings?tab=banking&edit=${accountId}`)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit Account
                                </DropdownMenuItem>
                              </>
                            )}
                            {isConnected && (account.source === 'mono' || account.open_banking_source === 'mono') && (
                              <DropdownMenuItem
                                data-testid={`update-bank-transactions-${accountId}`}
                                onClick={() => handleMonoUpdate(accountId)}
                                disabled={connecting}
                                className="text-slate-800"
                              >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Update bank transactions
                              </DropdownMenuItem>
                            )}
                            {isConnected && (account.source === 'mono' || account.open_banking_source === 'mono') && (
                              <DropdownMenuItem
                                data-testid={`force-reset-sync-${accountId}`}
                                onClick={async () => {
                                  // iter971k-Z · Turn 5i - escape hatch for
                                  // stuck "syncing" banners in production.
                                  // Clears mono_connections.sync_status and
                                  // bank_accounts.mono_sync_status if either
                                  // is stuck on "syncing". Idempotent + safe:
                                  // historical transactions are untouched.
                                  try {
                                    const { data } = await apiClient.post(
                                      `/open-banking-feeds/mono/force-reset-sync-status`,
                                      { bank_account_id: accountId }
                                    );
                                    toast.success(data.message || 'Sync status cleared.', { duration: 4000 });
                                    // Nudge banners to re-poll immediately.
                                    try {
                                      window.dispatchEvent(new CustomEvent('mono:sync-status-reset', {
                                        detail: { bank_account_id: accountId },
                                      }));
                                    } catch (_) { /* ignore */ }
                                  } catch (e) {
                                    const detail = e?.response?.data?.detail;
                                    toast.error(detail || 'Could not reset sync status: please try again.');
                                  }
                                }}
                                className="text-slate-800"
                              >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Clear stuck sync banner
                              </DropdownMenuItem>
                            )}
                            {/* iter1108 - Manage sub-accounts (Plaid update
                                mode with Account Select). Visible whenever
                                the account is connected via Plaid, so users
                                can ADD new sub-accounts they deselected at
                                initial link. If the connection currently has
                                multiple sub-accounts (a "group"), this is
                                also how they remove one without a full
                                disconnect. */}
                            {isConnected && account.source === 'plaid' && (
                              <DropdownMenuItem
                                onClick={() => openManageSubAccounts(account)}
                                className="text-sky-700"
                                data-testid={`manage-subaccounts-${accountId}`}
                                disabled={managingAccounts}
                              >
                                <Wallet className="w-4 h-4 mr-2" />
                                {getSubAccountSiblings(account)
                                  ? 'Manage sub-accounts'
                                  : 'Manage sub-accounts / add another'}
                              </DropdownMenuItem>
                            )}
                            {/* iter1112 - Plaid session-expiry / reauth-required
                                reconnect from the row. Surfaces whenever the
                                canonical `attention_reason === "needs_reconnect"`
                                flag is set (backend folds every Plaid item-error
                                state into that bucket). Uses Plaid Link update
                                mode so history stays attached. */}
                            {isConnected && account.source === 'plaid' &&
                             account.attention_reason === 'needs_reconnect' && (
                              <DropdownMenuItem
                                onClick={() => startPlaidRowReauth(account)}
                                disabled={plaidRowLoading === accountId}
                                className="text-amber-700"
                                data-testid={`reconnect-plaid-${accountId}`}
                              >
                                {plaidRowLoading === accountId ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Link2 className="w-4 h-4 mr-2" />
                                )}
                                <span className="font-medium">Reconnect bank sync</span>
                              </DropdownMenuItem>
                            )}
                            {isConnected && (
                              <>
                                <DropdownMenuItem onClick={() => promptDisconnect(account)} className="text-red-600">
                                  <Unlink className="w-4 h-4 mr-2" />
                                  Disconnect
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      </div>

                      {/* Expandable Ledger/Transactions Panel */}
                      {expandedAccount === accountId && (
                        <div className="bg-muted/20 border border-t-0 border-border rounded-b-xl overflow-hidden" data-testid={`ledger-panel-${accountId}`}>
                          {ledgerLoading ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mr-2" />
                              <span className="text-sm text-muted-foreground">Loading transactions...</span>
                            </div>
                          ) : ledgerData ? (
                            <div>
                              {/* Ledger Header */}
                              <div className="flex items-center justify-between px-4 py-3 bg-muted/40 border-b border-border">
                                <div className="flex items-center gap-3">
                                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    GL: {ledgerData.gl_code || 'N/A'}: {ledgerData.gl_name || ledgerData.bank_name}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {ledgerData.transaction_count} entries
                                  </Badge>
                                </div>
                                <div className="text-right">
                                  <span className="text-xs text-muted-foreground mr-2">Balance:</span>
                                  <span className={`text-sm font-bold ${ledgerData.current_balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {currency} {Math.abs(ledgerData.current_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                  </span>
                                </div>
                              </div>

                              {ledgerData.transactions.length === 0 ? (
                                <div className="text-center py-8">
                                  <Activity className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                                  <p className="text-sm text-muted-foreground">No transactions found. Connect your bank or upload a statement to see entries here.</p>
                                </div>
                              ) : (
                                <div className="max-h-[400px] overflow-y-auto">
                                  <table className="w-full text-sm">
                                    <thead className="sticky top-0 bg-muted/60 z-10">
                                      <tr className="text-xs text-muted-foreground uppercase tracking-wider">
                                        <th className="text-left px-4 py-2 font-medium">Date</th>
                                        <th className="text-left px-4 py-2 font-medium">Description</th>
                                        <th className="text-left px-4 py-2 font-medium">Ref</th>
                                        <th className="text-right px-4 py-2 font-medium">Debit</th>
                                        <th className="text-right px-4 py-2 font-medium">Credit</th>
                                        <th className="text-right px-4 py-2 font-medium">Balance</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {ledgerData.transactions.map((txn, idx) => (
                                        <tr 
                                          key={idx}
                                          className="border-t border-border/50 hover:bg-muted/30 transition-colors"
                                          data-testid={`ledger-row-${idx}`}
                                        >
                                          <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">
                                            {txn.date ? new Date(txn.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                                          </td>
                                          <td className="px-4 py-2.5 text-foreground max-w-[250px] truncate">
                                            {txn.description || '-'}
                                          </td>
                                          <td className="px-4 py-2.5 text-muted-foreground text-xs font-mono">
                                            {txn.reference || '-'}
                                          </td>
                                          <td className="px-4 py-2.5 text-right font-medium text-emerald-600 whitespace-nowrap">
                                            {txn.debit > 0 ? `${currency} ${txn.debit.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : ''}
                                          </td>
                                          <td className="px-4 py-2.5 text-right font-medium text-red-600 whitespace-nowrap">
                                            {txn.credit > 0 ? `${currency} ${txn.credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : ''}
                                          </td>
                                          <td className={`px-4 py-2.5 text-right font-bold whitespace-nowrap ${txn.balance >= 0 ? 'text-foreground' : 'text-red-600'}`}>
                                            {currency} {Math.abs(txn.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* iter1090 - Removed the "Enable Automatic Bank Sync" amber
            banner (previously at this position). It only rendered when
            `!plaidConfig.enabled && isPlaidCountry` OR `!monoConfig.enabled
            && country==='NG'` - i.e. when the backend was missing the
            Open Banking env vars. Its Setup CTA pointed users to
            `/settings?tab=connections` which is not something a business
            user can act on (it's a server-side deployment task). Removed
            per Feb-2026 UX audit (AfriStakes/US Test screenshot review). */}

        {/* Quick Actions Footer */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Button variant="outline" className="justify-start gap-2 h-auto py-3" data-testid="quick-review-transactions" onClick={() => navigate('/bank-feeds?tab=transactions&filter=pending')}>
            <AlertCircle className="w-4 h-4 text-amber-500" />
            <span className="text-sm">Review Transactions</span>
          </Button>
          <Button variant="outline" className="justify-start gap-2 h-auto py-3" data-testid="quick-ai-reconcile" onClick={() => navigate('/bank-feeds?tab=reconciliation')}>
            <TrendingUp className="w-4 h-4 text-[#108981]" />
            <span className="text-sm">AI Reconcile</span>
          </Button>
          <Button variant="outline" className="justify-start gap-2 h-auto py-3" data-testid="quick-general-ledger" onClick={() => navigate('/general-ledger')}>
            <FileText className="w-4 h-4" />
            <span className="text-sm">General Ledger</span>
          </Button>
          <Button variant="outline" className="justify-start gap-2 h-auto py-3" data-testid="quick-manage-accounts" onClick={() => navigate('/settings?tab=banking')}>
            <CreditCard className="w-4 h-4" />
            <span className="text-sm">Manage Accounts</span>
          </Button>
        </div>
      </div>

      {/* Open Banking Consent Dialog */}
      <OpenBankingConsentDialog
        open={showConsentDialog}
        onOpenChange={setShowConsentDialog}
        provider={consentProvider}
        onConsent={handleConsentGiven}
        onCancel={() => setPendingConnection(null)}
      />

      {/* Inter-Bank Transfer Modal */}
      <InterBankTransferModal
        open={showTransferModal}
        onOpenChange={setShowTransferModal}
        bankAccounts={bankAccounts}
        business={business}
        onSubmit={handleCreateTransfer}
      />

      {/* Upload Billing Confirmation Dialog */}
      <Dialog open={!!uploadBillingPreview} onOpenChange={(open) => { if (!open) handleDeclineUploadBilling(); }}>
        <DialogContent className="sm:max-w-md" data-testid="upload-billing-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#108981]" />
              Statement Processing Fee
            </DialogTitle>
            <DialogDescription>
              Older transactions in this statement will be processed and posted to your books.
            </DialogDescription>
          </DialogHeader>
          {uploadBillingPreview && (
            <div className="space-y-4 py-2">
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Transactions found</span>
                  <span className="font-semibold">{uploadBillingPreview.transaction_count}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Rate per transaction</span>
                  <span>{uploadBillingPreview.symbol}{uploadBillingPreview.per_transaction_rate}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-semibold">
                  <span>Total fee</span>
                  <span className="text-[#108981]">{uploadBillingPreview.symbol}{uploadBillingPreview.total_fee?.toLocaleString()}</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                This covers AI categorization, GL posting, and reconciliation for all {uploadBillingPreview.transaction_count} transactions. Your card on file will be charged.
              </p>
            </div>
          )}
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={handleDeclineUploadBilling}
              disabled={confirmingUploadBilling}
              data-testid="decline-upload-billing-btn"
            >
              Not now
            </Button>
            <Button
              onClick={handleConfirmUploadBilling}
              disabled={confirmingUploadBilling}
              className="bg-[#108981] hover:bg-[#108981]/90"
              data-testid="confirm-upload-billing-btn"
            >
              {confirmingUploadBilling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Confirm & Process
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter1108 - Plaid update-mode Account Select launcher. Mounted only
          while an active link_token is set on `manageAccountsFor`. */}
      {manageAccountsFor?.linkToken && (
        <PlaidManageAccountsLauncher
          linkToken={manageAccountsFor.linkToken}
          onSuccess={onManageAccountsSuccess}
          onExit={() => setManageAccountsFor(null)}
        />
      )}

      {/* iter1112 - Ad-hoc Plaid Link launcher for the 3-dots
          Connect / Reconnect items on individual bank rows. Reuses the
          Manage-Accounts launcher component (same react-plaid-link
          setup + auto-open on ready). */}
      {plaidRowLink?.linkToken && (
        <PlaidManageAccountsLauncher
          linkToken={plaidRowLink.linkToken}
          onSuccess={onPlaidRowSuccess}
          onExit={() => {
            if (plaidRowLink.mode === 'connect' && plaidRowLink.accountId) {
              recordConnectAbandoned({ bank_account_id: plaidRowLink.accountId, provider: 'plaid' });
            }
            setPlaidRowLink(null);
            try { sessionStorage.removeItem('akf_plaid_row_hint'); } catch { /* no-op */ }
          }}
        />
      )}

      {/* iter1108 - Pre-launch info dialog. Shown when a user clicks
          "Manage sub-accounts" on any Plaid row. Explains what will happen
          and clarifies the billing rule (sub-accounts under one login do
          NOT count toward the plan bank limit). */}
      <Dialog open={!!manageIntent} onOpenChange={(open) => !open && setManageIntent(null)}>
        <DialogContent className="sm:max-w-md" data-testid="manage-subaccounts-info-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-sky-700" />
              Manage sub-accounts for {manageIntent?.institution}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-2">
              We'll take you to your bank to choose which accounts are shared.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700 space-y-1">
              <p><span className="font-semibold">You can:</span></p>
              <ul className="list-disc pl-5 space-y-0.5">
                <li>Add new sub-accounts (e.g. a wallet you opened after the first connection)</li>
                <li>Remove specific sub-accounts you no longer want to sync</li>
                <li>Keep the connection active for the rest</li>
              </ul>
            </div>
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-900" data-testid="subaccounts-not-counted-note">
              <p>
                Sub-accounts under one bank login are free — they don't count as extra banks on your plan.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setManageIntent(null)} disabled={managingAccounts}>
              Cancel
            </Button>
            <Button
              onClick={confirmAndLaunchManageAccounts}
              disabled={managingAccounts}
              className="bg-sky-700 hover:bg-sky-800 text-white"
              data-testid="manage-subaccounts-continue-btn"
            >
              {managingAccounts ? 'Opening...' : 'Continue to bank'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect Warning Dialog */}
      <Dialog open={!!disconnectTarget} onOpenChange={(open) => { if (!open) setDisconnectTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="disconnect-warning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Disconnect Bank · Disruptive Action
            </DialogTitle>
            <DialogDescription className="text-left">
              You are about to disconnect <span className="font-semibold text-foreground">{disconnectTarget?.bankName}</span> from automatic bank feeds.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* iter1108 - if this connection covers multiple sub-accounts,
                spell that out honestly. Plaid Items are atomic; one
                revocation kills every sub-account under the login. */}
            {disconnectTarget?.subAccounts && disconnectTarget.subAccounts.length > 1 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="disconnect-siblings-warning">
                <p className="text-sm font-semibold text-amber-900 mb-2">
                  This will disconnect {disconnectTarget.subAccounts.length} sub-accounts sharing this login:
                </p>
                <ul className="text-sm text-amber-800 space-y-1 list-disc pl-5">
                  {disconnectTarget.subAccounts.map((sa) => (
                    <li key={sa.id} data-testid={`disconnect-sibling-${sa.id}`}>
                      <span className="font-medium">
                        {sa.institution_name || sa.bank_name || 'Bank'}
                      </span>
                      {' · '}
                      <span className="text-amber-700">
                        {(sa.native_currency || sa.currency || '').toUpperCase() || 'account'}
                        {sa.mask ? ` (····${sa.mask})` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-amber-800/90 mt-2">
                  To disconnect only some of them, use <span className="font-semibold">Manage sub-accounts</span> instead.
                </p>
              </div>
            )}
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
              <p className="text-sm font-semibold text-red-800">What will happen:</p>
              <ul className="text-sm text-red-700 space-y-2 list-disc pl-4">
                <li>Transactions will <span className="font-bold">no longer auto-feed</span> from this bank</li>
                <li>You will need to <span className="font-bold">manually upload statements</span> to keep your books updated</li>
                <li>Bank reconciliation will depend on manual statement uploads going forward</li>
              </ul>
            </div>
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
              <p className="text-sm font-semibold text-green-800">What stays safe:</p>
              <ul className="text-sm text-green-700 space-y-2 list-disc pl-4">
                <li>All existing GL accounts remain <span className="font-bold">unaffected</span></li>
                <li>All transactions already captured are <span className="font-bold">fully preserved</span></li>
                <li>Journal entries and balances are <span className="font-bold">not changed</span></li>
              </ul>
            </div>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDisconnectTarget(null)}
              disabled={disconnecting}
              data-testid="cancel-disconnect-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDisconnect}
              disabled={disconnecting}
              data-testid="confirm-disconnect-btn"
            >
              {disconnecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                <>
                  <Unlink className="w-4 h-4 mr-2" />
                  Yes, Disconnect
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staged Mono Sync Progress Dialog */}
      <MonoSyncProgress
        open={!!syncStatus}
        syncStatus={syncStatus}
        syncMessage={syncMessage}
        backlogInfo={backlogInfo}
        confirmingBacklog={confirmingBacklog}
        onConfirmBacklog={confirmBacklog}
        onRollback={rollbackConnection}
        onDismiss={() => { dismissBacklog(); resetState(); }}
        preflightData={preflightData}
        preflightLoading={preflightLoading}
        onNavigateToBankFeed={() => navigate('/bank-feeds')}
      />

      {/* iter971k-D · The blocking `MonoBackfillProgress` modal has been
          replaced with a global non-blocking banner mounted in App.js
          (`MonoCatchUpBanner`). Users are no longer trapped waiting for
          the catch-up: they can navigate anywhere while the banner tracks
          progress. Retained the state hook (`backfillFor`) only to avoid
          touching every setter callsite; the modal itself is gone. */}

      {/* Sprint 1 F1.5 - delete bank account confirmation */}
      <ConfirmDestructive
        name={pendingDeleteAccount?.name || ''}
        impact="Disconnects the bank and removes its access. Transaction history is hidden from new reports; journals are kept."
        destructiveLabel="Delete bank account"
        requireType
        open={!!pendingDeleteAccount}
        onOpenChange={(o) => { if (!o) setPendingDeleteAccount(null); }}
        onConfirm={async () => {
          if (pendingDeleteAccount?.id) {
            await deleteBankAccount(pendingDeleteAccount.id);
          }
          setPendingDeleteAccount(null);
        }}
        testIdPrefix="confirm-delete-bank"
      />

      {/* iter1238 Phase 8.9 - Cancel pending backlog upload confirmation */}
      <Dialog
        open={!!cancelBacklogTarget}
        onOpenChange={(o) => { if (!o && !cancellingBacklog) setCancelBacklogTarget(null); }}
      >
        <DialogContent className="max-w-md" data-testid="cancel-backlog-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              Cancel this upload?
            </DialogTitle>
            <DialogDescription>
              This will discard the pending backlog fee decision for
              <span className="font-semibold text-slate-800"> &ldquo;{cancelBacklogTarget?.job?.filename}&rdquo;</span>.
              No charge is made, no rows are imported. You can re-upload the same
              statement any time to try again with a fresh parse.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button
              variant="destructive"
              onClick={confirmCancelBacklog}
              disabled={cancellingBacklog}
              className="w-full gap-2"
              data-testid="confirm-cancel-backlog-btn"
            >
              {cancellingBacklog ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Yes, cancel this upload
            </Button>
            <Button
              variant="ghost"
              onClick={() => setCancelBacklogTarget(null)}
              disabled={cancellingBacklog}
              className="w-full"
              data-testid="keep-backlog-btn"
            >
              Keep it — I&apos;ll decide later
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 804 - Statement Vault dialogs (provider-neutral) */}
      <StatementUploadDialog
        open={!!statementUploadFor}
        bankAccount={statementUploadFor || {}}
        onClose={() => setStatementUploadFor(null)}
        onUploaded={fetchData}
      />
      {/* iter 990 - opening balance entry */}
      <SetOpeningBalanceDialog
        account={obDialogAccount || {}}
        open={!!obDialogAccount}
        onClose={() => setObDialogAccount(null)}
        onSaved={fetchData}
      />
      <StatementVaultDrawer
        open={!!statementVaultFor}
        bankAccount={statementVaultFor || {}}
        onClose={() => setStatementVaultFor(null)}
      />

      {/* iter1381 · Unified statement-upload starter (LLM-parser path).
          Opened by every per-account "Upload Statement" CTA: the hover
          chip, the 3-dot menu item and the HistoryGapBanner. Its
          "Select statement file" CTA clicks the per-account hidden
          <input id="upload-{id}"> which fires handleStatementUpload —
          the unchanged async backlog flow. */}
      <UploadStarterDialog
        open={!!uploadStarterAccountId}
        onOpenChange={(open) => { if (!open) setUploadStarterAccountId(null); }}
        bankAccountId={uploadStarterAccountId}
        onSelectFile={() => {
          const id = uploadStarterAccountId;
          setUploadStarterAccountId(null);
          setTimeout(() => {
            try { document.getElementById(`upload-${id}`)?.click(); } catch (_) { /* noop */ }
          }, 60);
        }}
      />

      {/* iter1384 · Fill Gap modal reopened from the amber chip on
          an account tile. Auto-cleared reminders never reach this
          state; only user-dismissed windows do. */}
      <PostDeleteGapFillModal
        open={!!openGapFillFor}
        onOpenChange={(o) => { if (!o) { setOpenGapFillFor(null); fetchGapReminders(); } }}
        bankAccountId={openGapFillFor?.bank_account_id}
        gapStart={openGapFillFor?.gap_start}
        gapEnd={openGapFillFor?.gap_end}
        reason={openGapFillFor?.reason}
        feedCheck={openGapFillFor?.feed_check}
        partial={openGapFillFor?.partial ? openGapFillFor : null}
        connected={(() => { const a = bankAccounts.find((x) => x.id === openGapFillFor?.bank_account_id); return !!a && (['mono', 'plaid', 'stitch'].includes(a.source) || a.open_banking_connected); })()}
        onManualUploadClick={(id) => {
          setOpenGapFillFor(null);
          openUploadStarterForAccount(id);
        }}
      />

      {/* iter1225 - Enrich international transfer details modal.
          Reuses AddBankAccountModal in edit mode so users can fill in
          the full account number, SWIFT/BIC and IBAN for a Plaid or
          Mono account. Saved payload flows through PUT /api/bank-accounts
          which strips history-locked fields backend-side. */}
      <AddBankAccountModal
        open={!!enrichingBank}
        onOpenChange={(o) => { if (!o) setEnrichingBank(null); }}
        editingBank={enrichingBank?.__new ? null : enrichingBank}
        onSuccess={() => { setEnrichingBank(null); fetchData(); }}
      />

      {/* iter1207 - Backlog confirmation dialog is now GLOBAL (mounted in
          DashboardLayout via GlobalBacklogModalProvider) so it surfaces on
          any page - not only /banks. Local plaidBacklog state stays in
          place because pollPlaidSyncStatus() still uses it to gate the
          BankConnectProgress splash, but rendering the dialog is owned by
          the provider. */}

      {/* iter1121 - progress splash bridging the 3-15s dead zone between
          Plaid Link closing and either the backlog dialog opening or the
          sync landing in a terminal state.
          iter1204-plaid-ux: `exchanging` phase stays blocking (short
          round-trip, dismissing creates orphan public_tokens); `polling`
          and `finishing` phases are now dismissible - the global
          BacklogProgressBanner in DashboardLayout takes over. */}
      <BankConnectProgress
        open={!!plaidConnectProgress && !plaidBacklog}
        phase={plaidConnectProgress?.phase}
        institutionName={plaidConnectProgress?.institutionName}
        onDismiss={() => setPlaidConnectProgress(null)}
      />
    </DashboardLayout>
  );
};

// iter875 - Wrap in BankFeedProvider so the centralised data layer is
// available throughout the page tree. The export remains a default
// React component so existing route imports keep working.
const MyBanksPageWithProvider = (props) => (
  <BankFeedProvider>
    <MyBanksPage {...props} />
  </BankFeedProvider>
);

export default MyBanksPageWithProvider;
